import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Outlet, NavLink, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useRutinaPorPagar } from '../hooks/useRutinaPorPagar';
import { urlDeFoto } from '../lib/fotoDePerfil';
import { seLeOfreceMantenimiento } from '../lib/salidaAMantenimiento';
import { seVendenPlanes } from '../lib/ventaEnLaApp';
import { planDelCatalogo, rutinaALaVentaEnLaApp } from '../lib/planAccess';
import { leer as leerLocal, escribir as escribirLocal } from '../lib/almacenLocal';
import { numerosDelDia, estadoDelDia } from '../lib/estadoDelDia';
import { plural, nombreDePlan } from '../lib/labels';
import { num1 } from '../lib/numeros';
import { vigilarContadores } from '../lib/contadoresDelCliente';
import { objetivoDelDia, varianteDelDia } from '../lib/estadoMacros';
import { aFecha, diaLocal, hoyLocal, fechaLargaDeHoy, plazoEnPalabras, diasDesdeHoy, pintarDia, fechaCorta, ahoraDeLaApp } from '../lib/horaEspana';
import { useEsTelefono } from '../lib/esTelefono';
import { abrirRutinaPdf } from '../lib/abrirRutina';
import { useOnboarding } from '../context/OnboardingContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
    Sun, Dumbbell, Apple, FileText, MessageCircle, User,
    LogOut, Bell, ChevronRight, CreditCard, Target, Bot,
    Flame, Activity, Scale, Search, SlidersHorizontal, Pill,
    ClipboardCheck, Menu, X, PanelLeftClose, PanelLeftOpen,
    CheckCircle2, Circle, Sparkles, LayoutDashboard, AlertTriangle, Phone, Clock, TrendingUp, Moon,
    CalendarRange, CalendarDays, Check
} from 'lucide-react';
import Logo12EN12 from '../components/Logo12EN12';
import { verComo } from '../lib/modoRevision';
import { ABIERTO_SIN_ACCESO, sinAccesoALaApp, puedeAbrir, leTocaElCuestionario } from '../lib/puertasDelCliente';
import { AJUSTE_YA_EN_MARCHA, abrirPagoDelAjuste, ajusteEnManosDelEquipo, seLeOfreceLaRevision } from '../lib/revision';
import LimiteDeError from '../components/LimiteDeError';
import ProgresoHoy from '../components/inicio/ProgresoHoy';
import NutricionHoy from '../components/inicio/NutricionHoy';
import ExtrasDelDia from '../components/nutrition/ExtrasDelDia';
import { filaDelCheckIn } from '../lib/checkInPendiente';
import { leQuedaLaSegundaParte } from '../lib/segundaParteDelPerfil';
import TarjetaInicio from '../components/inicio/TarjetaInicio';
import { useDiaDeHoy } from '../hooks/useDiaDeHoy';
import BottomNav from '../components/BottomNav';
import FraseAlEntrar from '../components/FraseAlEntrar';

// ===== Macro colors (identidad 12EN12) =====
const MACRO = { protein: '#FF671F', carbs: '#2196F3', fat: '#FFA500' };

// testids ASCII-estables (sin diacríticos)
const slug = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '-');

// ===== Shared brand bits =====
const JG12Logo = ({ size = 'md', tone = 'dark' }) => <Logo12EN12 size={size} tone={tone} />;

const PlanBadge = ({ plan, planName }) => {
    // EL NOMBRE DEL PLAN, NO SU CÓDIGO (#64 del informe del 15-08: «RETO12EN12_GOLD»).
    // Donde se pasaba `planName` ya salía bien; donde no -- la tabla de clientes y la de
    // rutinas -- caía en `plan.toUpperCase()` y el panel gritaba el código de la base. El
    // nombre está en el catálogo, que ya viaja en el contexto de sesión.
    const { planCatalog } = useAuth();
    // Quitar el sufijo "(legacy)" de la insignia: es info interna, no de cara al usuario.
    const label = (planName || (plan ? nombreDePlan(plan, planCatalog) : '') || '')
        .replace(/\s*\(legacy\)/i, '').trim();
    if (!label) return <span className="badge-silver opacity-70" data-testid="plan-badge">Sin plan</span>;
    const cls = {
        gold: 'badge-gold', silver: 'badge-silver', bronze: 'badge-bronze', elm: 'badge-elm',
        reto12en12_gold: 'badge-gold', reto12en12_silver: 'badge-silver',
    }[plan] || 'badge-elm';
    return <span className={cls} data-testid="plan-badge">{label}</span>;
};


/**
 * UN MACRO DEL DÍA: el número grande, el nombre y cuánto es el objetivo.
 *
 * Sustituye al anillo + barra del panel (documento del 10-08, pantalla 4). Tres decisiones,
 * y las tres son suyas:
 *
 *  - «Los números suben de tamaño y se les quita el anillo». El anillo ocupaba 92 px para
 *    decir lo mismo que dice el número.
 *  - «"de 190" DEBAJO del número, no arriba en una franja aparte»: antes había que mirar a
 *    dos sitios para saber cuánto faltaba.
 *  - El nombre del macro debajo del número y en negrita.
 *
 * La barra fina de abajo se queda: es lo que sustituye al anillo de un vistazo y ocupa, en
 * palabras del documento, «una décima parte». Cuando se pasa del objetivo se pone en rojo,
 * que es la única alarma de esta pantalla.
 */
const MacroGrande = ({ valor, objetivo, label, color }) => {
    const pct = objetivo > 0 ? Math.min((valor / objetivo) * 100, 100) : 0;
    const pasado = valor > objetivo + 4;
    return (
        <div className="text-center" data-testid={`macro-${slug(label)}`}>
            <p className="font-data font-bold leading-none text-foreground text-[34px] sm:text-[40px]">
                {Math.round(valor)}
            </p>
            <p className="text-sm font-bold mt-1.5" style={{ color: pasado ? '#DC2626' : color }}>{label}</p>
            <p className="text-sm text-muted-foreground font-data">de {Math.round(objetivo)}</p>
            <div className="h-1 bg-muted rounded-full overflow-hidden mt-2">
                <div className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${pct}%`, backgroundColor: pasado ? '#DC2626' : color }} />
            </div>
        </div>
    );
};

// ===== Checklist de primeros pasos (onboarding) =====
const OnboardingChecklist = ({ steps, onDismiss, navigate, onResume, showResume }) => {
    const doneCount = steps.filter(s => s.done).length;
    const pct = Math.round((doneCount / steps.length) * 100);
    return (
        <div className="surface p-5 relative overflow-hidden" data-testid="onboarding-checklist">
            <div className="absolute top-0 right-0 w-40 h-40 bg-brand/5 rounded-full blur-3xl pointer-events-none" />
            <button onClick={onDismiss} data-testid="dismiss-checklist-btn"
                className="absolute top-3 right-3 w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                title="Ocultar">
                <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-brand" />
                <p className="caption text-brand">Primeros pasos</p>
            </div>
            <h2 className="font-heading text-2xl font-bold uppercase text-foreground leading-none mb-1">Empieza aquí</h2>

            {/* Barra de progreso */}
            <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                    <div className="bg-brand h-2 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-xs font-data font-bold text-muted-foreground whitespace-nowrap">{doneCount}/{steps.length}</span>
            </div>

            <div className="space-y-2">
                {steps.map((s) => (
                    <button key={s.label} onClick={() => !s.done && navigate(s.path)}
                        disabled={s.done}
                        data-testid={`step-${s.id}`}
                        className={`w-full flex items-center gap-3 rounded-xl px-3.5 py-3 text-left transition-colors ${s.done ? 'bg-muted/50 cursor-default' : 'bg-muted/40 hover:bg-muted'}`}>
                        {s.done
                            ? <CheckCircle2 className="w-5 h-5 text-brand flex-shrink-0" />
                            : <Circle className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
                        <div className="min-w-0 flex-1">
                            <p className={`text-sm font-bold ${s.done ? 'text-muted-foreground line-through' : 'text-foreground'}`}>{s.label}</p>
                        </div>
                        {!s.done && <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
                    </button>
                ))}
            </div>

            {showResume && (
                <button onClick={onResume} data-testid="resume-tour-btn"
                    className="mt-4 w-full flex items-center justify-center gap-2 rounded-xl bg-brand/10 hover:bg-brand/15 text-brand font-bold text-sm py-3 transition-colors">
                    <Sparkles className="w-4 h-4" /> Continuar recorrido guiado
                </button>
            )}
        </div>
    );
};

// EL PLAN SE ACABÓ (punto 41 del doc del 07-08).
//
// La calculadora antigua ya lo tenía resuelto y es lo que se copia: se corta el acceso, se
// dice que la suscripción ha caducado y se le da salida. Y la salida de la que se habló:
// la Membresía, que es donde cae el que no renueva.
//
// EL «PONTE EN CONTACTO» ERA UNA PUERTA PINTADA (14-08-2026). Ofrecía escribir por
// WhatsApp y el número nunca llegó a ponerse, así que lo que salía era «escríbenos y lo
// vemos» sin decir a quién ni dónde. Ahora deja su correo y entra en Leads, que es donde
// alguien lo va a ver: «que deje su mail y queda como lead».
// LA VENTANA DE LAS 12 SEMANAS («Llevas 12 semanas sin actualizar tu porcentaje de
// grasa») YA NO SALE (27-10).


const PlanCaducado = ({ navigate, nombre, api, plan }) => {
    const { planCatalog } = useAuth();
    const [enviando, setEnviando] = useState(false);
    const [hecho, setHecho] = useState('');

    // SIN PEDIRLE EL CORREO (doc 19-08, «Mi plan y la baja»): «hoy dice "déjanos tu
    // correo y te escribimos", y el correo ya lo tenemos». Es SU sesión: un botón y ya.
    const pedirContacto = async () => {
        setEnviando(true);
        try {
            const r = await api.post('/leads/quiero-volver', {});
            setHecho(r.data?.mensaje || 'Hecho. Te escribimos enseguida.');
        } catch (err) {
            // Al cliente no se le enseña el detalle del backend: frase humana aquí y el
            // porqué a la consola, que es donde sirve.
            console.error('[caducado] no se pudo registrar el contacto', err);
            toast.error('No hemos podido apuntarlo. Inténtalo en un momento.');
        } finally {
            setEnviando(false);
        }
    };

    return (
        <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-2xl mx-auto animate-fade-in" data-testid="plan-caducado">
            <div className="surface p-8 text-center">
                <div className="w-16 h-16 bg-brand/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-8 h-8 text-brand" />
                </div>
                {/* «Plan», no «suscripción» (14-10): son pagos únicos, y la renovación ya
                    dice «Tu plan ha terminado». */}
                <h2 className="heading-2 text-foreground mb-2">Tu plan ha terminado</h2>

                {hecho ? (
                    <p className="text-brand font-medium text-sm" data-testid="caducado-hecho">{hecho}</p>
                ) : (
                    <div className="flex flex-col sm:flex-row gap-2 justify-center">
                        {/* SU PLAN, NO EL CATÁLOGO (Francisco, 25-08). Los dos botones de
                            esta pantalla traían a la misma lista entera, y al que pulsa
                            «Renovar mi plan» no hay que ponerle delante Premium, Gold y
                            Calculadora: ya ha dicho lo que quiere. Con `solo=mio` se le
                            enseña el suyo y, debajo, la puerta a los demás. «Ver mis
                            opciones», más abajo, sigue trayendo la lista completa. */}
                        <button onClick={() => navigate('/renovacion?solo=mio')} className="btn-brand whitespace-nowrap"
                            data-testid="caducado-renovar">
                            Renovar mi plan
                        </button>
                        <button onClick={pedirContacto} disabled={enviando}
                            className="text-brand text-sm hover:underline disabled:opacity-60 px-4 py-2.5"
                            data-testid="caducado-enviar">
                            {enviando ? 'Enviando...' : 'Que me escriban'}
                        </button>
                    </div>
                )}

                {/* La salida del que no renueva es Mantenimiento (doc 19-08, fallo 06:
                    «es donde aterriza todo el que termina un ciclo y no renueva»). Antes
                    aquí se ofrecía la Membresía de 97, que se vende por su propio embudo.
                    A quien YA está en Mantenimiento no se le ofrece (14-10,
                    `lib/salidaAMantenimiento`): solo renovar lo suyo. */}
                {seLeOfreceMantenimiento(planDelCatalogo(planCatalog, plan)) && (
                <div className="mt-6 pt-6 border-t border-border" data-testid="caducado-mantenimiento">
                    <p className="text-sm text-foreground font-medium mb-1">¿Prefieres seguir por tu cuenta?</p>
                    {/* A /renovacion Y NO A /planes (24-08). Este boton prometia
                        Mantenimiento y llevaba a la comparativa de 247, 847 y 1.500, donde
                        Mantenimiento no sale por ningun lado: /planes pinta solo los tres
                        niveles a proposito, porque la membresia no se compra, se aterriza
                        en ella. El unico sitio donde Mantenimiento existe para el cliente
                        es la pantalla de renovacion, como salida «Dejarlo por ahora»
                        (core/renovacion.py). Al que se le acaba el ciclo y estaba dudando,
                        enseñarle tres precios mas caros que el que acaba de leer es lo que
                        hace que se vaya.

                        Y SE DICE DONDE VA A CAER. Los dos botones de esta pantalla acaban en
                        la misma pagina, asi que el de abajo tenia que prometer solo lo que
                        hay: la salida esta ahi, pero es la ultima tarjeta y se titula
                        «Dejarlo por ahora», no «Mantenimiento». Quien no lo sabe cree que se
                        equivoco de boton y se va. La otra mitad -- que la pantalla de
                        renovacion baje sola hasta esa tarjeta -- es de RenovacionPage.jsx. */}
                    <button onClick={() => navigate('/renovacion')} className="text-brand text-sm hover:underline" data-testid="ver-membresia">
                        Ver mis opciones
                    </button>
                </div>
                )}
            </div>
        </div>
    );
};

// =============== INICIO NUEVO (T1 del doc del 16-08) ===============
//
// «Un bloque, "Lo que toca hoy", con macros, suplementación y entreno. Debajo,
// "Pendiente". Y arriba del todo, la frase del día.»
//
// Lo que cambia respecto al Inicio de siempre, que sigue vivo debajo:
//
//  - Los macros son el bloque «Tu dieta hoy» del doc del 21-08: el deslizador de cuatro
//    posiciones (Macros · Dieta · Llevas · Falta) y las comidas con su casilla. Vive en
//    `components/inicio/TuDietaHoy.jsx`, que es donde está explicada cada cuenta.
//  - Desaparece la pila de banners: lo pendiente es una lista de líneas iguales, en el
//    orden del documento, y cuando no queda nada se dice y ya está.
//  - La línea de entreno sale por el DATO (tiene rutina cargada), no por el plan; y si no
//    la marca se queda sin marcar. Nunca en rojo (regla 3 del doc).
//
// Todo esto vive detrás del interruptor `t1_inicio_nuevo` del panel: apagado, el cliente
// ve el Inicio de siempre sin que nadie despliegue nada.

// La fecha de hoy con la que viajan las dietas: LA DEL RELOJ DEL CLIENTE (bloque F,
// 23-08). Estuvo clavada a España «para coincidir con el servidor», pero eso le abría a
// un cliente en América un día que aún no había vivido y le marcaba la cena de su sábado
// en el domingo. Ahora el criterio es el contrario y es de producto: su día es el suyo;
// el servidor acepta la fecha del cliente en todos los caminos del día vivido
// (`dia_del_cliente`), y España queda para los plazos. Nutrición usa este mismo reloj.
const hoyDeLaDieta = () => hoyLocal();

// El día de mañana con el mismo criterio (el reloj del cliente): para la línea «Mañana»
// y para abrir Nutrición en ese día (`?date=`, el mismo camino que usa Mi semana).
const mananaDeLaDieta = () =>
    new Date(Date.parse(`${hoyLocal()}T00:00:00Z`) + 86400000).toISOString().slice(0, 10);

const estrellas = (n) => (n >= 1 && n <= 5 ? '★'.repeat(n) + '☆'.repeat(5 - n) : '');

// LA FRASE DEL DÍA, SIN PUNTO FINAL (punto 177 del 27-08): «es una frase suelta, no un
// párrafo; el punto la hace sonar a texto de sistema».
//
// Se limpia AL PINTAR y no en la base a propósito: las frases las carga un guión
// (backend/_cargar_frases.py) y las escribe gente, así que limpiar las de hoy no impide que
// la de mañana vuelva a venir con punto.
//
// Cae SOLO el punto suelto. Los suspensivos se quedan (son parte de la frase y quitarles uno
// los deja en dos, que es una errata), y la interrogación y la exclamación ni se tocan.
// Sin lookbehind a posta: no lo entienden los Safari anteriores al 16.4 y un `SyntaxError`
// en un fichero de éstos no rompe la frase, rompe la aplicación entera al cargar.
const sinPuntoFinal = (texto) => {
    const t = String(texto || '').trim();
    if (!t.endsWith('.') || t.endsWith('..')) return t;
    return t.slice(0, -1);
};

// Una línea de «Lo que toca hoy» o de «Pendiente»: título, detalle y, a la derecha, la
// flecha de entrar o el «✓ hecho». Nada de rojos ni de la palabra «pendiente».
const LineaDeHoy = ({ icono: Icono, titulo, detalle, cursiva, extra, hecho, onClick, testId, onDescartar }) => {
    const Contenedor = onClick ? 'button' : 'div';
    // «ENTENDIDO, NO ME LO VUELVAS A DECIR» (JDG, Trello 21-09). Solo en las líneas que lo
    // traen: el aviso del programa nuevo. Va debajo del detalle, como texto pequeño, y no
    // dispara el clic de la línea.
    const descarte = onDescartar ? (
        <button type="button" data-testid={testId ? `${testId}-descartar` : undefined}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); onDescartar(); }}
            className="mt-1.5 text-xs font-semibold text-brand hover:underline text-left">
            Entendido, no me lo vuelvas a decir
        </button>
    ) : null;
    return (
        <Contenedor onClick={onClick} data-testid={testId}
            className={`surface w-full p-4 flex items-center gap-4 text-left ${onClick ? 'surface-hover group' : ''}`}>
            {Icono && (
                <div className="w-11 h-11 bg-brand/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icono className="w-5 h-5 text-brand" />
                </div>
            )}
            <div className="min-w-0 flex-1">
                <p className="font-bold text-foreground text-sm">{titulo}</p>
                {/* EL DETALLE CABE ENTERO, NO SE CORTA (31-08). Iba con `truncate`, que en una
                    línea de cuatro palabras no molesta pero se comió justo lo que hay que
                    leer: la escalada del cierre del día salía «Llevas 2 días seguidos si...»,
                    y esa frase ES el trabajo. Se probó a dejarlo en dos líneas y la más larga
                    -- «Dejamos de recordártelo. Si te está costando, dínoslo y lo vemos» -- seguía
                    cortada a 390 px, así que se quita el recorte. Las demás filas de esta
                    lista llevan frases de cuatro palabras y no les cambia nada. */}
                {detalle && <p className={`text-muted-foreground text-sm${cursiva ? ' italic' : ''}`}>{detalle}</p>}
                {extra && <p className="text-muted-foreground text-xs mt-0.5">{extra}</p>}
                {descarte}
            </div>
            {hecho ? (
                <span className="flex items-center gap-1.5 text-sm font-semibold text-emerald-600 dark:text-emerald-400 flex-shrink-0"
                    data-testid={testId ? `${testId}-hecho` : undefined}>
                    <CheckCircle2 className="w-4 h-4" /> hecho
                </span>
            ) : onClick ? (
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-brand transition-colors flex-shrink-0" />
            ) : null}
        </Contenedor>
    );
};

// El «Te faltan 94 · de 120» por macro (MacroQueFalta) se retiró el 21-08: el doc de ese
// día lo sustituye por el deslizador de cuatro posiciones de `components/inicio/TuDietaHoy`,
// donde «Falta» es una de las vistas y sale de la misma cuenta.

// Los tres cierres del documento. Rotan por día: dentro del mismo día siempre el mismo,
// que si no cambiaría de frase cada vez que recarga.
const CIERRES = [
    'Hoy no tienes nada más pendiente. Otra semana más, sigue así que vas bien.',
    'Hoy no tienes nada más pendiente. Ahora a descansar, que ya toca.',
    'Hoy no tienes nada más pendiente.',
];

const InicioNuevo = () => {
    const { user, profile, api, can, appSettings, pantalla, habilitaciones, planCatalog, refreshProfile } = useAuth();
    // El ajuste a medida solo se ofrece aquí si una persona del equipo lo ha encendido, y
    // nunca a quien ya lo pagó.
    const ajusteDelPerfil = profile?.ajuste_a_medida || {};
    const ajusteOfrecido = !!ajusteDelPerfil.ofrecer_en_inicio && !ajusteDelPerfil.cobrado;
    const pagarElAjuste = async () => {
        try {
            const { url } = await abrirPagoDelAjuste(api);
            if (url) { window.location.href = url; return; }
            toast.success(AJUSTE_YA_EN_MARCHA, { siempre: true });
            await refreshProfile();
        } catch (e) {
            console.error('No se pudo abrir el pago del ajuste a medida', e);
            toast.error('No hemos podido abrir el pago. Inténtalo en un momento.');
        }
    };
    const navigate = useNavigate();
    const [comido, setComido] = useState({ P: 0, H: 0, G: 0 });
    // El objetivo del día ya resuelto por el servidor (el del día menos el peri): el mismo
    // número que la cabecera de Nutrición. No se recalcula aquí, y no hay segunda fuente:
    // mientras no llegue, arriba no hay números (ver `diaCargado`).
    const [objetivoComidas, setObjetivoComidas] = useState(null);
    // El documento entero del día (`GET /diets/{hoy}`): TuDietaHoy necesita las comidas
    // una a una para la lista de «Marca lo que ya te has comido».
    const [dieta, setDieta] = useState(null);
    const [diaCargado, setDiaCargado] = useState(false);
    // Los extras del día se apuntan al final de la columna Nutrición (components/inicio/NutricionHoy).
    // La configuración de dieta QUE TIENE PUESTA el cliente (`GET /user/diet-config`), que es
    // la que manda los días que todavía no ha montado. Aquí solo se usa el `opcion_peri`,
    // para el rótulo del perientreno: ver `conPerientreno`.
    const [configDieta, setConfigDieta] = useState(null);
    const [suplementos, setSuplementos] = useState([]);
    const [rutina, setRutina] = useState(null);
    const [entrenoDeHoy, setEntrenoDeHoy] = useState(null);
    // Lo que le toca hoy según el servidor: si tiene rutina, qué día es y si es descanso.
    const [entrenoDelDia, setEntrenoDelDia] = useState(null);
    // La rutina en PDF que sube el entrenador: para quien la tiene, ESA es su rutina
    // (mismo patrón que RoutinePage tras el arreglo del 21-08).
    const [pdfRutina, setPdfRutina] = useState(null);
    // La semana de su rutina (`GET /routines/semana`): de ahí sale el nombre de lo que
    // entrena hoy («Pectoral - Tríceps»), que es lo que pide el punto 24 del 24-08.
    const [semanaRutina, setSemanaRutina] = useState(null);
    // El día de mañana, para la línea «Mañana · aún sin dieta · Repetir la de hoy».
    const [dietaManana, setDietaManana] = useState(null);
    const [ultimoCierre, setUltimoCierre] = useState(null);
    // La ventana del cierre y la racha, resueltas por el servidor (doc «El día», 31-08).
    const [estadoDelCierre, setEstadoDelCierre] = useState(null);
    const [reportes, setReportes] = useState([]);
    const [tienePreferencias, setTienePreferencias] = useState(true);
    // LO QUE LE HAS CONTESTADO Y NO HA ABIERTO (doc «El día», 30-08, «El aviso que falta»).
    // Sus macros nuevos, su rutina del mes, su informe o un mensaje del chat. Vivía solo
    // dentro de la campanita, o sea que para enterarse había que entrar a mirar por si acaso.
    const [respuesta, setRespuesta] = useState(null);

    useEffect(() => {
        if (!profile) return;
        const cargar = async () => {
            // Cada petición con su red: una función que no está (el registro de entreno se
            // está construyendo) o un plan sin suplementación no pueden dejar Inicio en
            // blanco. Lo que no llega, no se pinta.
            // OJO: aquí NO se pide `/macros`. El objetivo del día no se compone de dos
            // fuentes -- ese fue el fallo: según cuál llegara antes, Inicio enseñaba 190, 235
            // o 225 del mismo día mientras el cliente miraba. Viene resuelto con la dieta o
            // no viene.
            const [dietaRes, suplRes, rutinaRes, logRes, cierreRes, estadoRes, dueRes, prefsRes,
                   pdfRes, mananaRes, semanaRes, cfgRes, respuestaRes] = await Promise.all([
                api.get(`/diets/${hoyDeLaDieta()}`).catch(() => ({ data: { exists: false } })),
                api.get('/supplements/current').catch(() => ({ data: null })),
                api.get('/routines/current').catch(() => ({ data: null })),
                api.get(`/workout-logs/hoy?fecha=${hoyLocal()}`).catch(() => ({ data: { log: null } })),
                api.get('/checkins?type=daily&limit=1').catch(() => ({ data: [] })),
                // QUÉ DÍA ESTÁ ABIERTO Y CUÁNTOS LLEVA SIN CERRAR (doc «El día», 31-08).
                // Viene resuelto del servidor, texto incluido: la escalada de los 2, 4 y 7
                // días es una regla de producto y no puede vivir en dos sitios.
                // Con la HORA del cliente además del día: la fila de la pesada se apaga a
                // las 12:00, y esas 12:00 son las suyas (bloque 4 del 1-09). El servidor
                // sigue decidiendo qué días toca; la pantalla solo dice qué hora es aquí.
                api.get(`/checkins/estado?fecha=${hoyLocal()}&hora_cliente=${ahoraDeLaApp().getHours()}`)
                    .catch(() => ({ data: null })),
                api.get('/reports/due').catch(() => ({ data: { items: [] } })),
                api.get('/user/preferences').catch(() => ({ data: { has_preferences: true } })),
                api.get('/routines/pdf/info').catch(() => ({ data: { hay: false } })),
                api.get(`/diets/${mananaDeLaDieta()}`).catch(() => ({ data: { exists: false } })),
                // QUÉ LE TOCA HOY, POR SU NOMBRE (punto 24 del doc del 24-08: «TU ENTRENO
                // PREVISTO · Pectoral - Tríceps · VER»). El servidor ya lo sabía -- el
                // reparto del PDF cruzado con los días que entrena -- y esta pantalla no lo
                // preguntaba: para el que tiene la rutina en PDF la línea decía «Tu rutina,
                // en PDF» y había que abrir el PDF para saber qué tocaba.
                api.get(`/routines/semana?hoy_cliente=${hoyLocal()}`).catch(() => ({ data: { hay: false } })),
                // LA CONFIGURACIÓN QUE TIENE PUESTA, para los días que aún no ha montado.
                // Es la MISMA petición que hace TuDietaHoy en ese caso para pedir el reparto,
                // y aquí hace falta por lo mismo: sin ella esta pantalla no sabe si el
                // cliente lleva perientreno y el rótulo del punto 49 se lo inventaba.
                api.get('/user/diet-config').catch(() => ({ data: null })),
                // «CUANDO TÚ LE CONTESTAS», el aviso que faltaba (doc «El día», 30-08). El
                // servidor devuelve el más nuevo sin abrir y nombra los demás: la cola de
                // Inicio es «una lista y un orden», no tres filas diciendo lo mismo.
                api.get('/notifications/respuesta').catch(() => ({ data: { hay: false } })),
            ]);
            // EN INICIO, SOLO LA SUYA. Desde el 18-08 el servidor manda la suplementación
            // general a quien no tiene la suya escrita, y ahí está bien: la pestaña tiene
            // que enseñar siempre algo. Pero «Lo que toca hoy · Tu suplementación» es lo
            // que le ha pautado él, y una lista general anunciada como suya en la portada
            // sería decirle que le hemos puesto algo que no le hemos puesto.
            setSuplementos(suplRes.data?.es_generica ? [] : (suplRes.data?.actual || []));
            setRutina(rutinaRes.data);
            setEntrenoDeHoy(logRes.data?.log || null);
            // Qué le toca hoy lo dice el SERVIDOR, que cuenta los días en hora de España.
            setEntrenoDelDia(logRes.data?.tiene_rutina ? logRes.data : null);
            setPdfRutina(pdfRes.data?.hay ? pdfRes.data : null);
            setSemanaRutina(semanaRes.data?.hay ? semanaRes.data : null);
            setConfigDieta(cfgRes.data || null);
            setDietaManana(mananaRes.data || null);
            setUltimoCierre(Array.isArray(cierreRes.data) ? cierreRes.data[0] || null : null);
            setEstadoDelCierre(estadoRes.data || null);
            setReportes(dueRes.data?.items || []);
            setTienePreferencias(!!prefsRes.data?.has_preferences);
            setRespuesta(respuestaRes.data?.hay ? respuestaRes.data : null);

            const dieta = dietaRes.data;
            setDieta(dieta || null);
            // Viene aunque el día no exista todavía: el objetivo es del día, no de lo que
            // haya puesto.
            setObjetivoComidas(dieta?.objetivo_comidas || null);
            // LO SERVIDO TAMBIÉN LO CUENTA EL SERVIDOR (punto 3 del 17-08).
            //
            // Aquí se sumaba `macros_efectivos` de cada alimento tal y como estuviera
            // guardado en la dieta. Nutrición no hace eso: pide la calibración del día y
            // pinta lo que le devuelve. Dos cuentas del mismo número, y la de aquí fallaba
            // en silencio cuando el campo no estaba guardado -- pasa, y entonces Inicio
            // decía «Te faltan 180 de 180» de un día que Nutrición daba por cerrado.
            // Ahora `servido_comidas` viene resuelto de `GET /diets/{fecha}`, con el
            // perientreno ya fuera, igual que `objetivo_comidas`.
            if (dieta?.servido_comidas) setComido(dieta.servido_comidas);
            setDiaCargado(true);
        };
        cargar().catch((err) => {
            // Al cliente no se le enseña una traza: si algo no llega, Inicio se pinta con
            // lo que tenga.
            console.error('[inicio] no se pudieron cargar los datos del día', err);
            setDiaCargado(true);
        });
    }, [api, profile]);

    // EL OBJETIVO, EL MISMO QUE ENSEÑA NUTRICIÓN. Lo resuelve el servidor con el reparto de
    // ese día, ya con el perientreno descontado. O ese, o ninguno: no hay segundo camino.
    // (TuDietaHoy le suma el peri por su cuenta para la vista «Macros», con el reparto vivo.)
    const objetivo = objetivoDelDia(objetivoComidas);
    const diaDeHoy = useDiaDeHoy({
        api, userId: user?.id, fecha: hoyDeLaDieta(), dieta, objetivo, activo: diaCargado && !!objetivo,
    });

    // El cierre del día de HOY (hora de España, que es como cuenta el backend).
    const cierreDeHoy = ultimoCierre && diaLocal(aFecha(ultimoCierre.created_at) || ahoraDeLaApp()) === hoyLocal()
        ? ultimoCierre : null;
    // AQUÍ SE PREPARABA LA LÍNEA «MIS SUPLEMENTOS» DE INICIO, que Jesús quitó el
    // 6-09-2026 (el hueco que dejó está más abajo, en el bloque de líneas del día).
    // `suplementosTomados` miraba la respuesta de suplementos del cierre de hoy para
    // ponerle la marca de hecho -- solo con el «sí» entero: «no todos» y «no» se
    // quedaban sin marca --, y `nombresSuplementos` juntaba los títulos sin repetir,
    // porque quien se toma el Whey en dos comidas lo leía dos veces en el mismo
    // párrafo. Sin línea que pintar, ninguno de los dos hace ya nada.
    //
    // El estado `suplementos` SÍ se queda: baja a «Tu dieta hoy» para que cada comida
    // enseñe el suyo debajo (punto 174 del 27-08).

    // La línea de entreno sale por el dato, no por el plan (regla 3): si tiene rutina
    // cargada para hoy. El día de descanso no se anuncia: no hay nada que hacer.
    //
    // QUÉ DÍA ES HOY LO DICE EL SERVIDOR, NO EL NAVEGADOR. Esto miraba
    // `ahoraDeLaApp().toLocaleDateString(...)`, o sea el día del aparato del cliente, mientras
    // la cabecera de arriba escribe el día de España: a un cliente que no esté en España la
    // misma pantalla le decía «Lunes, 17 de agosto» y buscaba su entreno en el domingo, así
    // que un día de descanso ahí le borraba la línea de entreno del lunes. El backend ya lo
    // resuelve en `GET /workout-logs/hoy`, que desde el bloque F recibe el día del cliente.
    const grupoMuscular = entrenoDelDia?.dia_rutina || '';
    const esDiaDeEntreno = !!entrenoDelDia && !entrenoDelDia.descanso;
    // Lo previsto para hoy en la semana de su rutina: el que tiene la rutina en PDF no
    // tiene «rutina estructurada», pero su reparto sí dice qué grupo le toca hoy.
    //
    // SOLO SI PUEDE ABRIR LA RUTINA. `GET /routines/semana` contesta sin mirar el plan («si
    // su entrenador le subió la rutina, es suya»), pero /dashboard/routine va detrás de
    // CAP.RUTINA -- el plan más el interruptor t3_entreno -- y CapabilityRoute devuelve a
    // Inicio sin decir nada. Sin esa llave, la línea del PDF se queda como estaba y abre el
    // PDF, que es lo único que sí funciona para él: mejor un enlace pobre que uno muerto.
    const hoyDeLaSemana = (semanaRutina && can('rutina')) ? (semanaRutina.hoy || null) : null;
    const grupoPrevisto = hoyDeLaSemana?.entrena ? (hoyDeLaSemana.grupo || '') : '';
    const descansoPrevisto = !!hoyDeLaSemana && !hoyDeLaSemana.entrena;
    const soloCardio = esDiaDeEntreno && entrenoDelDia.tipo === 'cardio';
    const duracionCardio = (() => {
        const d = entrenoDelDia?.cardio_minutos;
        if (d == null || d === '') return '';
        return /^\d+$/.test(String(d).trim()) ? `${d} minutos` : String(d);
    })();
    const resumenEntreno = [estrellas(entrenoDeHoy?.estrellas),
        entrenoDeHoy?.pesos?.[0]?.ejercicio
            ? `${entrenoDeHoy.pesos[0].ejercicio}${entrenoDeHoy.pesos[0].peso_kg != null ? ` ${num1(entrenoDeHoy.pesos[0].peso_kg)} kg` : ''}`
            : ''].filter(Boolean).join(' · ');

    // (Aquí se calculaba `tipoDeDia` para el título; desde el 25-10 el título es siempre
    // «Mi día», ver la cabecera.)

    // EL DÍA PUEDE EXISTIR SIN DIETA MONTADA: desde el 24-08 apuntar un extra hace upsert
    // del documento del día, así que `exists` ya no significa «configurado». El marcador
    // bueno es `num_comidas`, que solo lo escribe un guardado de dieta de verdad (la misma
    // regla y por la misma razón que `diaConfigurado` en components/inicio/TuDietaHoy.jsx).
    const diaConfigurado = Boolean(dieta?.exists && dieta.num_comidas);

    // EL PERIENTRENO YA NO SE ROTULA DESDE AQUÍ (puntos 86 a 88 del artifact del 25-08).
    // Desde el 24-08 había una frase, «Tus macros de hoy llevan el perientreno dentro», y
    // para decidir si salía se recalculaba aquí la configuración del día -- con su propia
    // precedencia, que tenía que coincidir con la de `TuDietaHoy` o el rótulo describía otro
    // número. Ahora en su sitio hay un INTERRUPTOR, y vive donde viven los números, así que
    // lee el mismo reparto que ellos y no puede desmentirlos: components/inicio/TuDietaHoy.

    // El PDF se abre vía blob porque el visor del navegador no manda el token (mismo
    // camino que RoutinePage y EntrenoPage).
    // «Que directamente, si tienes rutina, que le abra» (vídeo del 27-08). Un toque y se abre.
    // Aquí estaba el fallo que él vivió en directo -- «toco y no abre» --: la ventana se pedía
    // después de bajar el fichero y el iPhone la bloqueaba en silencio. Ver lib/abrirRutina.
    const abrirPdfRutina = () => abrirRutinaPdf(api);

    // ── 4.3: PLAN POR PLAN, POR EL CATÁLOGO (habilitaciones), jamás por nombre de plan ──
    const h = habilitaciones || {};
    // «Tus macros los pones tú · Cambiarlos»: los planes de autogestión (el catálogo lo
    // dice con `edita_macros`). Gold y Premium lo llevan a false y no ven la tarjeta.
    const ponesTusMacros = can('edita_macros') && !!objetivo;
    // «La rutina del mes» sin rutina cargada: Calculadora y ELM (`rutina: del_mes`).
    // `can('rutina')` añade el interruptor t3 del panel, el mismo que abre la ruta.
    //
    // Y SOLO SI LA HAY (ficha 39 del parte de Gonzalo, 4-09). La tarjeta salía por el
    // catálogo del plan y ofrecía «Ver la de este mes» aunque el equipo no hubiera subido
    // ninguna para su sexo: se tocaba y la pantalla estaba vacía. Si la hay lo dice el
    // servidor en el perfil (`rutina_del_mes.hay`, en `perfil_del_cliente` de routes/users,
    // resuelto con `pdf_del_mes_vigente` y su sexo), que es quien la entrega; aquí solo se
    // pinta una cosa u otra. Mientras el perfil no conteste (undefined) no se promete nada.
    //
    // OJO CON EL ORDEN DE ABAJO: cuando SÍ hay PDF del mes para él, `GET /routines/pdf/info`
    // ya lo resuelve por sexo (`_el_pdf_que_le_toca`, 25-08) y manda la rama de `pdfRutina`,
    // que es mejor: «Tu rutina · Abrirla» y se abre de un toque. Así que en la práctica lo
    // que se pinta aquí es el caso que faltaba: el mes que todavía no está.
    const planConRutinaDelMes = h.rutina === 'del_mes' && can('rutina');
    const hayRutinaDelMes = profile?.rutina_del_mes?.hay === true;
    const rutinaDelMes = planConRutinaDelMes && hayRutinaDelMes;
    // «Escribir a tu entrenador»: el apartado de contacto de los planes sin entrenador,
    // si su catálogo trae un canal de verdad (el de solo incidencias técnicas no lo es).
    const canalContacto = (h.canal_contacto || '').trim();
    const escribirAlEntrenador = h.acompanamiento === 'solo_app' && canalContacto
        && !/^ninguno$/i.test(canalContacto) && !/incidencias/i.test(canalContacto);
    // La tarjeta de venta del que no lleva rutina pero puede comprarla (`rutina: opcional`,
    // hoy Mantenimiento). El precio es el del complemento del catálogo («Suelta»), que
    // desde el 24-08 son 57 € como en todas partes: si el catálogo no lo dice, no hay
    // tarjeta. El número NO se escribe aquí, para que no vuelva a haber dos.
    const precioRutinaSuelta = (planCatalog?.rutina_mes?.precios || [])
        .find((p) => /suelta/i.test(p.label || ''))?.importe;
    // Y SOLO SI SE VENDE EN LA APP (Francisco, 14-10: «la rutina no se compra dentro de la
    // app»): apagada en el catálogo, Inicio no la ofrece ni pide pagarla.
    const rutinaALaVenta = rutinaALaVentaEnLaApp(planCatalog);
    const ventaRutina = h.rutina === 'opcional' && !entrenoDelDia && !rutina && !pdfRutina
        && precioRutinaSuelta != null && rutinaALaVenta;
    // La marcó en su reporte y no la ha pagado (04-10): la tarjeta lleva al pago, no a Mensajes.
    const rutinaPorPagar = useRutinaPorPagar({ api, activo: h.rutina === 'opcional' && rutinaALaVenta, alPagar: refreshProfile, userId: user?.id });

    // ── La línea de mañana: a Nutrición en el día de mañana (?date=, como Mi semana) ──
    //
    // «MAÑANA» A SECAS SE LEÍA COMO «LA MAÑANA» (revisión del 2-09), y justo debajo hay
    // otra fila que sí habla de la mañana de verdad («ayer no cerraste el día»), así que
    // las dos juntas confundían. Se dice qué día es: «Mañana, jueves».
    // Lo que hay HOY, para saber si se puede ofrecer repetirlo (ficha 17). Se cuenta igual
    // que dentro: comidas principales con alimentos dentro, el peri fuera.
    const hoyTieneComidas = Object.entries((dieta?.exists && dieta.comidas) || {})
        .filter(([k]) => k !== 'Intra' && k !== 'Post')
        .some(([, c]) => (c?.alimentos || []).length > 0);
    const manana = (() => {
        const d = dietaManana;
        const comidas = (d?.exists && d.comidas) || {};
        const conAlimentos = Object.entries(comidas)
            .filter(([k]) => k !== 'Intra' && k !== 'Post')
            .filter(([, c]) => (c?.alimentos || []).length > 0).length;
        // El nombre del día, con el reloj del cliente, que es el mismo con el que se
        // calcula qué día es «mañana» unas líneas más arriba.
        const nombreDelDia = new Date(`${mananaDeLaDieta()}T12:00:00`)
            .toLocaleDateString('es-ES', { weekday: 'long' });
        const titulo = `Mañana, ${nombreDelDia}`;
        // «REPETIR LA DE HOY» SOLO SI HOY HAY ALGO QUE REPETIR (ficha 17 del parte de
        // Gonzalo, 4-09). Esto miraba únicamente el día de MAÑANA, así que con hoy vacío
        // ofrecía copiar una dieta que no existe: el cliente tocaba, llegaba a Nutrición y
        // no había nada que traerse. Sin nada que copiar, lo que toca es crearla.
        if (!conAlimentos) {
            // Y `repetir` es lo que hace que el rótulo cumpla: sin él, «Repetir la de hoy»
            // solo abría mañana (ver el botón, más abajo).
            return { titulo, detalle: 'Sin dieta · todavía no la has creado',
                accion: hoyTieneComidas ? 'Repetir la de hoy' : 'Crearla',
                repetir: hoyTieneComidas, planificado: false };
        }
        // EL ESTADO SALE DE `lib/estadoDelDia`, QUE ES EL MISMO QUE USA NUTRICIÓN (Jesús,
        // 10-09): «se calcula una vez y se escribe igual en todas partes. Hoy Inicio se
        // inventa el suyo». Se lo inventaba: medía el objetivo en `macros_snapshot.P`
        // cuando ahí las claves son `P_total`, así que salía cero, ningún macro pasaba del
        // margen y la línea jamás llegaba a decir cuánto falta.
        // Y LOS NÚMEROS, LOS DEL SERVIDOR (13-09). Aquí se calculaban a mano desde el
        // `macros_snapshot` y los `macros_efectivos` de cada alimento, que es justo lo que
        // NO tienen los días montados sin pasar por la calculadora (repetir un día, una
        // favorita, una plantilla): salían objetivo cero y servido cero, y la línea se
        // quedaba en «dieta creada, sin terminar» de una dieta terminada. Medido con la
        // cuenta de Jesús del 14-09: el servidor decía 169,4 de 170 y esta cuenta, 0 de 0.
        // `numerosDelDia` pide los del servidor y solo calcula si no vienen, que es el
        // mismo orden que ya sigue el día de hoy unas líneas más arriba.
        const { objetivo, servido } = numerosDelDia(d);
        const estado = estadoDelDia(servido, objetivo);

        // Completo es completo: el día cierra dentro del margen. `is_cuadrado` sigue
        // valiendo como atajo -- es lo que escribe Nutrición al guardar -- pero ya no es lo
        // único, porque un día que cierra con dos gramos de desvío no lo lleva puesto.
        if (estado === 'listo' || d.is_cuadrado) {
            return { titulo, detalle: '✓ Dieta lista · no le falta nada', accion: 'Verla' };
        }
        // Y si se pasa, se dice, que es el otro caso que no puede quedarse en «sin terminar».
        if (estado === 'se_pasa') {
            return { titulo, detalle: 'Dieta creada · te pasas de algún macro', accion: 'Cuadrarla' };
        }
        // SIN OBJETIVO NO SE JUZGA, tampoco aquí. `estadoDelDia` devuelve `null` cuando no
        // hay con qué medir, y esta línea se lo comía: caía al «sin terminar» de abajo y le
        // decía al cliente que su dieta está a medias sin haberla medido. Si no se sabe, se
        // dice lo único que consta -- que está creada -- y se le deja verla.
        if (estado === null) {
            return { titulo, detalle: 'Dieta creada', accion: 'Verla' };
        }
        // SIN EL «· FALTAN 15 G» (Jesús, 28-09, audio sobre la captura de Inicio): «pone
        // dieta creada sin terminar y pone falta 15 gramos... eso quítalo, eso no tiene que
        // estar». Lo que falta se ve al entrar en el día; aquí solo el estado.
        return {
            titulo,
            detalle: 'Dieta creada, sin terminar',
            accion: 'Terminarla',
        };
    })();

    // ── Lo pendiente, en el orden del documento ──
    const pendientes = [];
    // EL ALTA SIN TERMINAR, LA PRIMERA (ficha 03 del parte de Gonzalo, 4-09). Al que tiene
    // acceso y NO ha contestado el alta no se le pedía nada: entraba en el Inicio entero con
    // los macros que hubiera en su ficha -- calculados sin sus datos -- y ninguna tarjeta se
    // lo decía, porque «Completar perfil» exige justo lo contrario (tener el alta hecha).
    // En la base de dev son 24 fichas con `status: registrado`.
    //
    // Y NO SE LE ECHA DE LA APP: al que no es «activo» no se le redirige al cuestionario
    // (eso dejó a 124 fichas en un callejón sin salida el 18-08, ver `?completar=1`). Se le
    // dice, arriba del todo, y él decide cuándo.
    if (profile && !profile.questionnaire_completed) {
        pendientes.push({
            id: 'alta', icono: ClipboardCheck, titulo: 'Termina tu alta',
            path: '/questionnaire',
        });
    }
    // SIN PLAN, LO PRIMERO ES ELEGIRLO (bug del 20-08): el recién registrado que volvía
    // atrás desde /planes se quedaba sin ningún camino para volver a verlos.
    // CON LOS PLANES CERRADOS (15-10, `lib/ventaEnLaApp`) no se le manda a elegirlo.
    if (profile && !profile.plan && seVendenPlanes()) {
        pendientes.push({
            id: 'plan', icono: User, titulo: 'Elige tu plan',
            path: '/planes',
        });
    }
    // LO QUE TÚ LE HAS CONTESTADO (doc «El día», 30-08, «El aviso que falta»).
    //
    // «Cuando revisas su reporte y le pones los macros nuevos, le subes la rutina del mes o le
    // dejas el informe, algo tiene que decírselo. El reporte le promete "te digo algo antes
    // del viernes a las tres", pero no hay nada escrito sobre cómo se entera de que ya está:
    // hoy tendría que entrar a mirar por si acaso.»
    //
    // Los avisos ya se escribían, pero SOLO dentro de la campanita, que es justo el sitio al
    // que hay que entrar a mirar. Aquí se le pone delante.
    //
    // VA ARRIBA, pero detrás del alta y del plan: es «el único aviso de toda la app que le da
    // algo en vez de pedirle algo, así que es el que más quiere recibir», y a la vez al que
    // todavía no ha contestado el alta ni ha elegido plan hay que decirle eso antes (ficha 03
    // del parte del 4-09, que es lo más reciente). En la práctica no se cruzan: al que no ha
    // terminado el alta nadie le ha puesto todavía unos macros.
    //
    // EL TEXTO ES EL DEL PROPIO AVISO, sin reescribirlo aquí: «Ya tienes tus macros nuevos»,
    // «Tu informe está listo»... La misma cosa dicha en dos sitios tiene que sonar igual.
    //
    // Y SE VA AL TOCARLA: al entrar en su pantalla se marca ese aviso leído, que es lo que la
    // quita de aquí y de la campanita a la vez. Sin eso, la fila seguiría ahí al volver.
    if (respuesta?.hay) {
        pendientes.push({
            id: 'respuesta', icono: MessageCircle,
            titulo: respuesta.titulo, detalle: respuesta.detalle,
            extra: respuesta.extra,
            path: respuesta.link || '/dashboard/messages',
            antesDeIr: () => {
                setRespuesta(null);
                if (respuesta.id) api.put(`/notifications/${respuesta.id}/read`).catch(() => {});
            },
        });
    }
    // LO QUE LE QUEDA DE CICLO, CUANDO YA SE ACABA (ficha 92 del parte de Gonzalo, 4-09).
    // Esto solo estaba en la campana, que es donde menos se mira: el que no la abre se entera
    // de que se le acaba el plan el día que deja de entrar. A siete días o menos, aquí.
    //
    // LOS DÍAS SE CUENTAN EN ESPAÑA, no con el reloj del cliente: el final del ciclo es una
    // fecha del negocio, como los plazos de los reportes, y desde América un mismo día se
    // contaría de dos maneras. La fecha ya viene resuelta en el perfil (`renovacion.fecha`,
    // que es `current_period_end` o el fin del ciclo calculado). Las palabras son LAS MISMAS
    // que las del aviso de la campana que había (se eliminó el 17-10): la misma cosa dicha
    // en dos sitios tiene que sonar igual o parecen dos cosas.
    //
    // Y VA ARRIBA DEL TODO, NO EN LA COLA DE «PENDIENTE» (Gonzalo, ficha 92; comprobado
    // el 13-09). Su recado es «en Inicio, ARRIBA, los días antes», y en la cola caía a
    // 2.089 px de una página de 2.659 en el teléfono: debajo de las comidas, de los
    // extras, del entreno y de la línea de mañana. O sea, donde no se ve sin bajar hasta
    // el final, que es el mismo sitio donde ya estaba la campana. Es la única fila de
    // Inicio que no va del día de hoy sino de si mañana sigue habiendo plan, así que no
    // compite con «Pendiente»: se pinta antes que nada, debajo del saludo.
    // Y A QUIEN STRIPE LE COBRA SOLO, NADA (16-10): su plan no acaba, se cobra el lunes.
    const diasDeCiclo = profile?.renovacion?.fecha && !profile?.renovacion?.vencida
        && !profile?.renovacion?.se_renueva_solo
        ? -diasDesdeHoy(String(profile.renovacion.fecha).slice(0, 10)) : null;
    const finDePlan = (diasDeCiclo != null && diasDeCiclo >= 0 && diasDeCiclo <= 7) ? {
        id: 'ciclo', icono: CalendarDays,
        // «TU PLAN» Y NO «TU CICLO» (Jesús, punto 6 del 9-09). Este número sale de
        // `renovacion.fecha`, que es cuándo se le acaba LO PAGADO, y no de su ciclo de
        // entrenamiento. Diciendo «ciclo» a los dos, el cliente leía «semana 10 de 12»
        // y «tu ciclo acaba mañana» en la misma pantalla. Las palabras siguen siendo
        // las mismas que las del aviso de la campana, que cambió con esto.
        titulo: diasDeCiclo > 1 ? `Tu plan acaba en ${diasDeCiclo} días`
            : diasDeCiclo === 1 ? 'Tu plan acaba mañana' : 'Tu plan acaba hoy',
        path: '/renovacion',
    } : null;
    // LA PESADA DE LA MAÑANA («Todo lo validado antes del 1 de septiembre», bloque 4): los
    // dos días de la pareja de la que sale su peso semanal, y hasta las 12:00, «porque
    // después de comer ya no es un peso en ayunas». Quién y cuándo lo dice el servidor
    // (`toca_pesarse` en core/series_cliente); aquí solo se pinta.
    //
    // VA LA PRIMERA, delante incluso del reporte: es la única de la cola que se muere hoy a
    // mediodía -- el reporte aguanta hasta mañana --, y además es lo que hace que el paso 1
    // del quincenal le salga ya resuelto.
    //
    // Y LLEVA AL CAMPO, no a buscarlo: «al tocarla le abre el campo en Seguimiento».
    //
    // FUERA DE MI DÍA (Enrique, 29-10): «Hoy toca pesarte» ya no sale, ni como fila ni como
    // hito. El check-in diario pide el peso todos los días.
    // EL REPORTE VA ANTES QUE EL CIERRE («Todo lo validado antes del 1 de septiembre», «La
    // cola de Inicio»): «el reporte entra arriba y empuja al check-in hacia abajo. Va
    // primero porque tiene FECHA DE ENTREGA; el check-in solo tiene el día de hoy».
    //
    // Estaba el último de la lista, detrás del perfil y de los alimentos preferentes, o sea
    // que el jueves por la tarde -- el día que se acaba el plazo -- lo urgente salía debajo
    // de tres cosas que pueden esperar semanas.
    //
    // Y EL AVISO DEL PLAZO NO ES UNA FILA MÁS: es esta misma, con otro texto. «Así el jueves
    // por la tarde sigue habiendo dos filas, no tres.» Lo hace `plazoEnPalabras`, que el
    // último día dice «Solo recordarte que tienes hasta hoy a las ocho».
    const reportesPendientes = [];
    reportes.forEach((r) => {
        // PASADA LA HORA DESAPARECE. Un plazo vencido en Inicio solo sirve para recordarle
        // cada mañana que llega tarde a algo que ya no puede mandar.
        const plazo = plazoEnPalabras(r.deadline);
        if (!r.is_open || r.overdue || !plazo || plazo.pasado) return;
        reportesPendientes.push({
            // «Tu reporte quincenal», no «Reporte quincenal»: en Inicio es una lista de
            // cosas suyas, y las de al lado también le hablan a él.
            id: `reporte-${r.tipo}`, icono: FileText,
            titulo: `Tu ${(r.tipo_label || 'reporte').toLowerCase()}`,
            detalle: plazo.texto, path: '/dashboard/reports',
        });
    });
    // «CHECK-IN DIARIO», SIEMPRE Y SIEMPRE EL DE HOY (25-10). Es una fila fija, como la de
    // la rutina, con la llave del plan (`cierre_dia`, decisión de Jesús del 24-08). Ya no
    // hay ventana: se rellena a cualquier hora y el de ayer no se puede rellenar. Lo único
    // que cambia es la marca de hecho, cuando el de hoy ya está guardado.
    //
    // Y A MEDIAS (artefacto de Enrique, 26-10): «Rellenar», «Continuar» con lo que ya tiene
    // guardado, o hecho. La regla vive en `lib/checkInPendiente.filaDelCheckIn`.
    const checkInDiario = filaDelCheckIn(estadoDelCierre || { hecho: !!cierreDeHoy });
    pendientes.push(...reportesPendientes);
    const faltaElAjuste = profile?.questionnaire_completed && !profile?.ajuste_macros_completado
        && !profile?.macros_puestos_por_alguien;
    const faltaElNivel1 = leQuedaLaSegundaParte(profile, can('macros_personalizados'));
    // DICE CUÁL DE LAS DOS LE FALTA (Jesús, 11-09, punto 19: «"Completar perfil" sigue
    // saliendo después de haberlo completado»).
    //
    // No mentía -- a él le falta el cuestionario largo --, pero acababa de contestar el alta
    // entera y la tarjeta le pedía «completar perfil» con las mismas palabras: leído desde
    // fuera, la app no se había enterado de lo que acababa de hacer.
    //
    // Son dos cosas distintas y ahora se nombran: ajustar los macros, o la segunda parte del
    // perfil. Medido el 11-09: 165 de los 171 activos están en la segunda, así que esta
    // tarjeta la ve casi todo el mundo y conviene que diga qué es.
    if (faltaElAjuste || faltaElNivel1) {
        pendientes.push({
            id: 'perfil', icono: User,
            titulo: faltaElAjuste ? 'Ajustar tus macros' : 'Te queda la segunda parte del perfil',
            // Corto: en 390 px el subtítulo se cortaba en «Para terminar de ajustar t...»
            // (recorrido móvil del 23-08). La línea es de una sola fila a propósito.
            path: faltaElAjuste ? '/questionnaire?ajustar=1' : '/questionnaire',
        });
    }
    if (!tienePreferencias) {
        pendientes.push({
            id: 'preferentes', icono: Apple, titulo: 'Alimentos preferentes',
            path: '/dashboard/nutrition',
        });
    }
    // LAS FOTOS DEL CUESTIONARIO, PENDIENTES HASTA QUE ESTÉN (César, Trello 21-09): «Lo
    // hago luego» vale, pero el cuestionario inicial tiene que seguir apareciendo como
    // tarea hasta que las fotos estén cargadas. El perfil trae cuántas hay
    // (`fotos_iniciales_subidas`); con cero, la tarea sigue aquí y lleva a subirlas.
    //
    // Y NO A LA VEZ QUE «TE QUEDA LA SEGUNDA PARTE DEL PERFIL» (Enrique, 02-11): con esa
    // tarjeta en pantalla, la de las fotos sobra.
    const saleLaSegundaParte = faltaElNivel1 && !faltaElAjuste;
    if (profile?.questionnaire_completed && profile?.fotos_iniciales_subidas === 0 && !saleLaSegundaParte) {
        pendientes.push({
            id: 'fotos-cuestionario', icono: ClipboardCheck,
            // SIN «LAS DEJASTE PARA LUEGO» (ALT-16, 14-10): a quien entra por el alta corta
            // nunca se le pidieron («te las pediremos en tu primer reporte»), y la tarea le
            // decía que las había aplazado él. El texto vale para los dos caminos.
            titulo: 'Tus fotos de partida',
            detalle: 'De frente, de perfil y de espaldas. Súbelas cuando las tengas',
            path: '/dashboard/reports?abrir=fotos',
        });
    }
    // LOS QUE YA ESTABAN DENTRO (bloque 6 del doc del 18-08). Lo que pregunta el alta nueva
    // solo lo contesta quien entre a partir de ahora: de los que ya pagan, ninguno tiene
    // biotipo, ni peso máximo, ni mejor forma. Es el dato que alimenta el modelo, así que se
    // les pasa el básico aquí dentro -- solo lo que falte -- y CON LA RAZÓN POR DELANTE, que
    // es lo que hace que lo contesten: no «rellena tu ficha», sino qué ganan.
    //
    // No se le enseña al que todavía tiene el perfil largo a medias: ese ya tiene su tarjeta
    // y son el mismo recorrido; dos avisos de «contesta cosas» a la vez no son dos avisos,
    // son ruido.
    const queFaltaDelBasico = (() => {
        if (!profile || !profile.questionnaire_completed || faltaElNivel1) return [];
        return [
            [!profile.biotype && profile.sex !== 'mujer', 'tu biotipo'],
            [!profile.peso_maximo, 'tu peso máximo'],
            // «Si no, pásala» (doc 23-08) es una respuesta: quien la dio no tiene mejor
            // forma que contar y no se le persigue con la tarjeta.
            [!profile.peso_mejor_momento && !profile.mejor_forma_pasada, 'tu mejor forma'],
            // Las proteínas ya no se preguntan (doc 23-08, punto 14: en su lugar van las
            // exclusiones, que pueden estar vacías legítimamente). Contarlas aquí dejaba
            // a todo cliente nuevo con una tarjeta imposible de quitar.
            [!profile.tiempo_intentandolo, 'cuánto llevas intentándolo'],
            [!profile.training_experience, 'tu experiencia entrenando'],
            [!profile.birthdate, 'tu fecha de nacimiento'],
        ].filter(([falta]) => falta).map(([, texto]) => texto);
    })();
    if (queFaltaDelBasico.length) {
        pendientes.push({
            id: 'completar-basico', icono: ClipboardCheck,
            // «NOS FALTA INFORMACIÓN» (Jesús, 11-09, punto 8). El título era «Nos faltan
            // cosas tuyas», que suena a reproche y no dice qué. El suyo dice qué pasa y
            // el cuerpo va derecho a la lista.
            titulo: 'Nos falta información',
            extra: queFaltaDelBasico.slice(0, 3).join(' · ') + (queFaltaDelBasico.length > 3 ? '...' : ''),
            path: '/questionnaire?completar=1',
        });
    }
    // La frase del día no se pinta en este Inicio (14-10): desde el 15-10 sale a pantalla
    // completa al iniciar sesión (components/FraseAlEntrar).

    const irA = (path, antes) => () => { antes?.(); navigate(path); };
    const mananaPlanificada = manana.planificado !== false;
    const nombreDeRutina = esDiaDeEntreno
        ? (entrenoDeHoy?.hecho ? 'Hecho hoy' : soloCardio ? 'Hoy toca cardio' : (grupoMuscular || grupoPrevisto || 'Hoy entrenas'))
        : (entrenoDelDia?.descanso || descansoPrevisto) ? 'Hoy descansas'
            : grupoPrevisto || (pdfRutina ? 'Tu rutina'
                : planConRutinaDelMes ? (rutinaDelMes ? 'La rutina del mes' : 'Pendiente este mes') : 'Ver mi rutina');
    const cuantos = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

    return (
        <div className="pb-10 animate-fade-in" data-testid="inicio-nuevo">
            <div className="px-4 sm:px-6 lg:px-12 xl:px-20 max-w-[1400px] mx-auto pt-6 space-y-6">
                {/* LA CABECERA COMO LA DE MI SEMANA Y NUTRICIÓN (14-10): el título en mayúsculas
                    con el mismo cuerpo, y debajo, en naranja, de qué va el día. «Tu dieta hoy»
                    vive aquí y no encima de los anillos, que lo decían dos veces seguidas. */}
                {/* LA CABECERA COMO LA DE NUTRICIÓN (Enrique, 16-10): fuera «Tu día, en resumen».
                    Arriba la fecha de hoy en naranja y mayúsculas, como «Nutrición · octubre»; debajo,
                    en blanco y grande como «Viernes 16 · Hoy», el tipo de día. Sin tipo, «Mi día»
                    (Francisco, 23-10: era «Hoy»). */}
                <header>
                    <p className="text-xs font-bold uppercase tracking-[0.15em] text-brand" data-testid="inicio-fecha">
                        {fechaLargaDeHoy()}
                    </p>
                    {/* «MI DÍA», SIEMPRE, Y DEL TAMAÑO DEL DE MI SEMANA (25-10). Antes decía «Día de
                        descanso» o «Día de entreno» a 26 px. */}
                    <h1 className="font-heading text-[34px] lg:text-5xl font-bold text-foreground leading-none pt-1.5" data-testid="inicio-tipo-dia">
                        Mi día
                    </h1>
                </header>

                {finDePlan && (
                    <TarjetaInicio icono={finDePlan.icono} tono="ambar" titulo={finDePlan.titulo}
                        detalle={finDePlan.detalle} testId="pendiente-ciclo" onClick={irA(finDePlan.path)} />
                )}

                {!diaCargado ? (
                    <div className="rounded-2xl border border-border bg-card p-6 animate-pulse" data-testid="macros-cargando">
                        <div className="h-4 w-32 bg-muted rounded" />
                        <div className="h-40 bg-muted rounded-xl mt-4" />
                    </div>
                ) : objetivo && (
                    <ProgresoHoy llevas={diaDeHoy.llevas} objetivo={diaDeHoy.objetivo} />
                )}

                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] gap-6 items-start">
                    <div className="min-w-0">
                        {!diaCargado ? null : objetivo ? (
                            <NutricionHoy api={api} fecha={hoyDeLaDieta()} tomas={diaDeHoy.tomas}
                                marcar={diaDeHoy.marcar} extras={diaDeHoy.extras} setExtras={diaDeHoy.setExtras}
                                repartoResuelto={diaDeHoy.repartoResuelto} navigate={navigate} />
                        ) : (
                            <div className="space-y-3">
                                <TarjetaInicio icono={Scale} tono="brand" titulo="Configura tus macros"
                                    accion="mas" testId="inicio-sin-macros"
                                    onClick={() => navigate('/dashboard/macro-calculator')} />
                                {/* Sin macros también se apunta un extra (puntos 26 y 29: «el campo
                                    está en Inicio», sin condiciones; no cuenta contra nada, punto 28). */}
                                <ExtrasDelDia api={api} fecha={hoyDeLaDieta()} extras={diaDeHoy.extras}
                                    origen="inicio" plegable compacto
                                    onAnadido={(e) => diaDeHoy.setExtras((prev) => [...prev, e])}
                                    onQuitado={(id) => diaDeHoy.setExtras((prev) => prev.filter((x) => x.id !== id))} />
                            </div>
                        )}
                    </div>

                    <div className="min-w-0 space-y-3">
                        {diaCargado && (
                            <TarjetaInicio icono={CalendarDays} tono="azul" titulo={manana.titulo}
                                detalle={mananaPlanificada ? 'Planificado' : 'Sin planificar todavía'}
                                accion={mananaPlanificada ? 'flecha' : 'Planificar'} testId="linea-manana"
                                onClick={irA(`/dashboard/semana?dia=${mananaDeLaDieta()}`)} />
                        )}
                        {can('suplementacion') && (
                            <TarjetaInicio icono={Pill} tono="brand" titulo="Mis suplementos"
                                detalle={suplementos.length ? cuantos(suplementos.length, 'suplemento', 'suplementos') : null}
                                testId="linea-suplementos"
                                onClick={irA('/dashboard/supplements')} />
                        )}
                        {can('rutina') && (
                            <TarjetaInicio icono={Dumbbell} tono="violeta" titulo="Mi rutina" detalle={nombreDeRutina}
                                testId="linea-entreno" onClick={irA('/dashboard/routine')} />
                        )}
                        {ventaRutina && !rutinaPorPagar.porPagar && (
                            <TarjetaInicio icono={Dumbbell} tono="violeta"
                                titulo={`Rutina del mes · ${Math.round(precioRutinaSuelta)} €`}
                                testId="linea-venta-rutina" onClick={irA('/dashboard/routine')} />
                        )}
                        {rutinaPorPagar.porPagar && (
                            <TarjetaInicio icono={Dumbbell} tono="violeta"
                                titulo={rutinaPorPagar.yendo ? 'Un momento...'
                                    : `Paga tu rutina del mes${precioRutinaSuelta != null ? ` · ${Math.round(precioRutinaSuelta)} €` : ''}`}
                                testId="linea-pagar-rutina" onClick={rutinaPorPagar.pagar} />
                        )}
                        {can('cierre_dia') && (
                            <TarjetaInicio icono={ClipboardCheck} tono="verde" titulo="Check-in diario"
                                hecho={checkInDiario.hecho} accion={checkInDiario.accion}
                                detalle={checkInDiario.detalle}
                                testId="linea-check-in-diario" onClick={irA('/dashboard/checkins')} />
                        )}

                        {/* SIN «SEGUIMIENTO» NI «REGISTRAR PESO» (25-10): las dos filas fijas se
                            quitan de Mi día. Seguimiento sigue en el menú, y el peso se apunta
                            en «Check-in diarios». El miércoles del pesaje vuelve a salir el hito
                            «Hoy toca pesarte» (abajo), que iba fundido en «Registrar peso». */}

                        {pendientes.map((p) => (
                            <TarjetaInicio key={p.id} icono={p.icono} tono={p.id === 'respuesta' ? 'brand' : 'ambar'}
                                titulo={p.titulo} detalle={p.detalle} testId={`pendiente-${p.id}`}
                                onClick={irA(p.path, p.antesDeIr)} />
                        ))}

                        {/* EL HITO DEL PESO, SOLO SI NO LO DICE YA LA COLA (25-10): con la fila
                            «Hoy toca pesarte» de los pendientes serían dos para lo mismo. Sin
                            ella, el hito es lo único que se lo recuerda. */}
                        {/* Y el hito del peso tampoco (29-10): «Hoy toca pesarte» fuera de Mi día. */}
                        {profile?.hito_de_hoy && profile.hito_de_hoy.tipo !== 'peso' && (
                            <div>
                                {/* «Hoy toca pesarte» (Francisco, 23-10): el hito es de hoy, y en
                                    Mi semana se queda «Toca pesarte», que allí va por días. */}
                                <TarjetaInicio icono={CalendarDays} tono="azul"
                                    titulo={profile.hito_de_hoy.tipo === 'peso' ? 'Hoy toca pesarte' : profile.hito_de_hoy.titulo}
                                    detalle={profile.hito_de_hoy.hora ? `A las ${profile.hito_de_hoy.hora}` : profile.hito_de_hoy.detalle}
                                    testId="linea-hito-de-hoy"
                                    onClick={profile.hito_de_hoy.link ? irA(profile.hito_de_hoy.link) : undefined} />
                                {profile.hito_de_hoy.id_descarte && (
                                    <button type="button" className="text-xs text-muted-foreground hover:text-foreground mt-1 ml-1"
                                        onClick={async () => {
                                            try {
                                                await api.post('/clients/hitos/descartar', { clave: profile.hito_de_hoy.id_descarte });
                                                await refreshProfile();
                                            } catch { toast.error('No se pudo guardar. Prueba otra vez.'); }
                                        }}>
                                        Quitar
                                    </button>
                                )}
                            </div>
                        )}
                        {diaCargado && ponesTusMacros && (
                            <TarjetaInicio icono={SlidersHorizontal} titulo="Tus macros" detalle="Cambiarlos"
                                testId="linea-mis-macros" onClick={irA('/dashboard/macro-calculator')} />
                        )}
                        {ajusteOfrecido && (
                            <TarjetaInicio icono={Sparkles} tono="brand" titulo="Ajuste a medida · 87 €"
                                testId="linea-ajuste-a-medida" onClick={pagarElAjuste} />
                        )}
                        {escribirAlEntrenador && (
                            <TarjetaInicio icono={MessageCircle} titulo="Escribir al equipo"
                                testId="linea-contacto" onClick={irA('/dashboard/messages')} />
                        )}
                        {profile?.ajuste_macros_completado && seLeOfreceLaRevision(profile, can) && (
                            <button type="button" onClick={irA('/dashboard/revision')} data-testid="revision-suelta-linea"
                                className="text-xs text-brand hover:brightness-110 ml-1">
                                Solicitar ajuste personalizado
                            </button>
                        )}
                        {ajusteEnManosDelEquipo(profile) && (
                            <p className="text-xs text-muted-foreground ml-1" data-testid="revision-suelta-pendiente">
                                Ajuste personalizado pagado: lo estamos preparando.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};


// =============== CLIENT DASHBOARD ===============

const ClientDashboard = () => {
    const { user, profile, api, myPlan, planUnpaid, can, pantalla } = useAuth();
    // El Inicio nuevo del doc del 16-08 (T1), detrás de su interruptor. Apagado -- que es
    // como nace -- aquí no cambia nada: se sigue viendo el Inicio de siempre.
    const inicioNuevo = pantalla('t1_inicio_nuevo');
    const enTelefono = useEsTelefono();
    const { resumeTour, active: tourActive, completed: tourCompleted } = useOnboarding();
    const navigate = useNavigate();
    const [routine, setRoutine] = useState(null);
    // ¿Hay rutina en PDF subida por su entrenador? Sin esto, el cliente con PDF y sin
    // rutina estructurada leía aquí «Sin rutina asignada» teniendo la suya subida.
    const [hayPdfRutina, setHayPdfRutina] = useState(false);
    const [unreadMessages, setUnreadMessages] = useState(0);
    const [macros, setMacros] = useState(null);
    const [todayConsumed, setTodayConsumed] = useState({ P: 0, H: 0, G: 0 });
    // EL OBJETIVO DEL DÍA, EL MISMO QUE ENSEÑA NUTRICIÓN (16-08-2026).
    //
    // Inicio pintaba el objetivo CRUDO del cliente (120 P · 50 H) y Nutrición el del día ya
    // repartido, que suma el perientreno (135 P · 65 H). Mismo día, mismo cliente, dos
    // pantallas seguidas y quince gramos de diferencia en dos macros. El día guardado ya
    // trae el total bueno en `macros_snapshot`, así que se usa ese y solo se cae al crudo
    // cuando todavía no hay día montado.
    const [totalDelDia, setTotalDelDia] = useState(null);
    const [hasPreferences, setHasPreferences] = useState(true); // optimista: evita parpadeo del checklist
    const [hasDiet, setHasDiet] = useState(false);
    // POR CLIENTE (punto 4.7). Esta bandera dice si ya cerró el checklist, y guardada sin
    // dueño el segundo que entrara en el mismo navegador se lo encontraba cerrado sin haberlo
    // tocado. La verdad vive en su perfil, en el servidor; esto es solo caché.
    const [checklistDismissed, setChecklistDismissed] = useState(false);
    useEffect(() => {
        if (user?.id) setChecklistDismissed(leerLocal('onboarding-checklist-dismissed', user.id) === '1');
    }, [user?.id]);
    const [dashDataLoaded, setDashDataLoaded] = useState(false);
    const [dueReports, setDueReports] = useState([]);

    // La compra de la revisión suelta vive ahora en su pantalla (/dashboard/revision):
    // desde aquí solo se entra, que es lo que pide el documento del 06-08-2026.

    // El cierre/completado vive en el perfil (backend); localStorage es solo caché local.
    useEffect(() => {
        if (profile?.checklist_dismissed) {
            escribirLocal('onboarding-checklist-dismissed', user?.id, '1');
            setChecklistDismissed(true);
        }
    }, [profile?.checklist_dismissed, user?.id]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // El día LOCAL del cliente, no el UTC (bloque F, 23-08): `toISOString`
                // pedía los macros de mañana desde media tarde en América.
                const today = hoyLocal();
                const [routineRes, pdfRes, messagesRes, macrosRes, dietRes, prefsRes, dueRes] = await Promise.all([
                    api.get('/routines/current').catch(() => ({ data: null })),
                    api.get('/routines/pdf/info').catch(() => ({ data: { hay: false } })),
                    api.get('/messages/unread-count?vista=cliente').catch(() => ({ data: { count: 0 } })),
                    api.get('/macros').catch(() => ({ data: null })),
                    api.get(`/diets/${today}`).catch(() => ({ data: { exists: false } })),
                    api.get('/user/preferences').catch(() => ({ data: { has_preferences: true } })),
                    api.get('/reports/due').catch(() => ({ data: { items: [] } })),
                ]);
                setRoutine(routineRes.data);
                setHayPdfRutina(!!pdfRes.data?.hay);
                setUnreadMessages(messagesRes.data.count);
                setDueReports(dueRes.data.items || []);
                setMacros(macrosRes.data);
                setHasPreferences(!!prefsRes.data?.has_preferences);
                const diet = dietRes.data;
                let dietHasFood = false;
                if (diet && diet.exists && diet.comidas) {
                    let totalP = 0, totalH = 0, totalG = 0;
                    Object.values(diet.comidas).forEach(meal => {
                        (meal.alimentos || []).forEach(a => {
                            const ef = a.macros_efectivos || {};
                            totalP += ef.P || 0; totalH += ef.H || 0; totalG += ef.G || 0;
                            dietHasFood = true;
                        });
                    });
                    setTodayConsumed({ P: Math.round(totalP * 10) / 10, H: Math.round(totalH * 10) / 10, G: Math.round(totalG * 10) / 10 });
                    const snap = diet.macros_snapshot;
                    if (snap && (snap.P_total || snap.H_total || snap.G_total)) {
                        setTotalDelDia({ P: snap.P_total || 0, H: snap.H_total || 0, G: snap.G_total || 0 });
                    }
                }
                setHasDiet(dietHasFood);
                setDashDataLoaded(true);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            }
        };
        // Con el Inicio nuevo encendido, esto no se pinta: no hay por qué pedir las seis
        // cosas que alimentan la pantalla vieja.
        if (profile && !inicioNuevo) fetchData();
    }, [api, profile, inicioNuevo]);

    const dismissChecklist = useCallback(() => {
        escribirLocal('onboarding-checklist-dismissed', user?.id, '1');
        setChecklistDismissed(true);
        api.patch('/clients/onboarding', { checklist_dismissed: true }).catch(() => {});
    }, [api, user?.id]);

    // Al completar los 3 pasos una vez, el checklist queda cerrado para siempre
    // (si no, el paso "primer día de comidas" volvería a salir incompleto cada día).
    useEffect(() => {
        if (!dashDataLoaded || checklistDismissed || !profile) return;
        const mt0 = macros?.training || profile?.macros_training;
        const done = !!(mt0 && (mt0.protein || mt0.proteinas)) && hasPreferences && hasDiet;
        if (done) dismissChecklist();
    }, [dashDataLoaded, checklistDismissed, profile, macros, hasPreferences, hasDiet, dismissChecklist]);

    // AL QUE SE LE ACABÓ EL PLAN NO SE LE DA LA BIENVENIDA (punto 41 del doc del 07-08).
    // Hasta ahora el cliente que llevaba un año pagando y terminaba su ciclo veía la misma
    // pantalla que el que acaba de registrarse: "Bienvenido a 12EN12, selecciona un plan".
    // No es lo mismo no haber empezado que haber terminado. El servidor dice cuál de los
    // tres casos es (profile.acceso.motivo).
    // `?ver=caducado` (solo equipo) enseña esta pantalla sin tener que caducarle el plan a
    // nadie: es la salida del que no renueva y hasta ahora no había forma de repasarla.
    if (profile?.acceso?.motivo === 'caducado' || verComo(user) === 'caducado') {
        return <PlanCaducado navigate={navigate} nombre={user?.name} api={api} plan={profile?.plan} />;
    }
    // Y SIN PLAN, AUNQUE TENGA FICHA (29-08). Esto miraba `!profile`, así que solo salía si
    // el perfil no existía; al que se registra Y se le crea la ficha con `plan: null` -- que
    // es lo normal desde el alta -- se le pintaba el Inicio entero: la frase del día, sus
    // macros, todo. Quien decide es `acceso`, que ya viene calculado del servidor
    // (`estado_de_acceso`) y distingue los tres casos.
    const sinPlanTodavia = profile?.acceso?.activo === false
        && ['sin_plan', 'sin_pagar'].includes(profile?.acceso?.motivo);
    if (!profile || planUnpaid || sinPlanTodavia) {
        return (
            <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-2xl mx-auto animate-fade-in">
                <div className="surface p-8 text-center">
                    <div className="w-16 h-16 bg-brand/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Target className="w-8 h-8 text-brand" />
                    </div>
                    <h2 className="heading-2 text-foreground mb-2">Te damos la bienvenida a 12EN12</h2>
                    {/* CON LOS PLANES CERRADOS (15-10, `lib/ventaEnLaApp`): el plan lo da el
                        equipo, así que no se le manda a elegirlo; se le invita a escribir. */}
                    {seVendenPlanes() ? (<>
                    {(planUnpaid || profile?.acceso?.motivo === 'sin_pagar') && (
                        <p className="text-muted-foreground mb-6 text-sm">
                            El pago de tu plan no llegó a completarse. Elige un plan para terminar la compra.
                        </p>
                    )}
                    <button onClick={() => navigate('/onboarding')} className="btn-brand inline-flex items-center gap-2" data-testid="onboarding-btn">
                        Seleccionar plan <ChevronRight className="w-4 h-4" />
                    </button>
                    </>) : (<>
                    <p className="text-muted-foreground mb-6 text-sm" data-testid="sin-plan-sin-venta">
                        Todavía no tienes un plan activo. Si tienes cualquier duda, escríbenos.
                    </p>
                    <button onClick={() => navigate('/dashboard/messages')} className="btn-brand inline-flex items-center gap-2" data-testid="sin-plan-escribenos">
                        Escríbenos <ChevronRight className="w-4 h-4" />
                    </button>
                    </>)}
                </div>
            </div>
        );
    }

    // A partir de aquí, el Inicio de siempre. El nuevo se pinta entero aparte para no
    // dejar la pantalla vieja llena de condiciones: mientras el interruptor esté apagado,
    // lo de abajo es exactamente lo que había.
    if (inicioNuevo) return <InicioNuevo />;

    const mt = macros?.training || profile?.macros_training;
    const mr = macros?.rest || profile?.macros_rest;
    // El perientreno y de dónde salen los macros ya no se leen aquí: se quitó el pie de la
    // tarjeta el 13-08 y no queda nadie en esta pantalla que los use.
    const hasMacros = mt && (mt.protein || mt.proteinas);

    const getP = (m) => m?.protein || m?.proteinas || 0;
    const getH = (m) => m?.carbs || m?.hidratos || 0;
    const getG = (m) => m?.fat || m?.grasas || 0;

    const checklistSteps = [
        { id: 'macros', label: 'Tus macros están calculados', done: !!hasMacros, path: '/dashboard/nutrition' },
        { id: 'preferences', label: 'Configura tus preferencias de comida', done: hasPreferences, path: '/dashboard/nutrition' },
        { id: 'diet', label: 'Prepara tu primer día de comidas', done: hasDiet, path: '/dashboard/nutrition' },
    ];
    const showChecklist = !checklistDismissed && checklistSteps.some(s => !s.done);

    // Ciclo del plan: nº de semanas (null si es mensual indefinido / variable).
    // EL TOTAL CON EL QUE SE CONTÓ LA SEMANA, NO EL DEL CATÁLOGO (24-08). Aquí se leía
    // `myPlan.ciclo.semanas` y la semana la calcula el servidor con core/cycle, que busca el
    // plan sin resolver alias: con un perfil migrado escrito «CalMa» no le encontraba el
    // ciclo, la semana crecía sin dar la vuelta y salía «Semana 63/12». `cycle_total_weeks`
    // viene de esa misma cuenta, así que los dos números van siempre a la par.
    const cicloSemanas = profile.cycle_total_weeks ?? null;
    const weekProgress = cicloSemanas ? Math.min((profile.week / cicloSemanas) * 100, 100) : null;
    const todayRoutine = routine?.days?.find(d =>
        d.day.toLowerCase() === ahoraDeLaApp().toLocaleDateString('es-ES', { weekday: 'long' }).toLowerCase());
    const isRestDay = todayRoutine?.is_rest;
    const activeTarget = isRestDay ? mr : mt;

    // UN AVISO CADA VEZ, EL DE MÁS ARRIBA QUE CUMPLA (documento del 10-08, pantalla 4).
    //
    // Aquí salían apilados: el reporte que toca, la checklist de primeros pasos, el de
    // ajustar macros, el de elegir plan y el del perfil a medias. Cinco tarjetas grandes,
    // una debajo de otra, antes de llegar a nada. Cuando le pides cinco cosas a la vez no
    // le estás pidiendo ninguna.
    //
    // El orden es el del documento, adaptado a los avisos que hoy tienen dato de verdad.
    // Los que faltan por poder calcularlos -- «estos son tus macros provisionales» y «ya
    // puedes hacer tu revisión mensual» -- van cuando exista de dónde sacarlos, y su sitio
    // es esta lista.
    //
    // Y SOLO EN EL TELÉFONO: en escritorio se apilan como estaban, que ahí caben y el
    // rediseño todavía no ha llegado a esa vista. `enTelefono` mira el mismo corte que usa
    // Tailwind para `lg` (1024 px), porque esto no se puede resolver con una clase: no es
    // ocultar una tarjeta, es elegir cuál de las cinco se pinta.
    const avisoPendiente = (() => {
        if (!enTelefono) return null;                                 // en escritorio, todos
        if (!myPlan) return 'plan';                                   // sin plan no puede hacer nada
        if (profile?.questionnaire_completed && !profile?.ajuste_macros_completado
            && !profile?.macros_puestos_por_alguien) return 'ajustar';
        if (leQuedaLaSegundaParte(profile, can('macros_personalizados'))) return 'perfil';
        if (dueReports.length) return 'reporte';
        if (showChecklist) return 'checklist';
        return null;
    })();
    // En el teléfono manda la lista de arriba (uno solo); en escritorio, la condición de
    // siempre de cada tarjeta, que es como estaba.
    const sale = (id, condicionDeSiempre) => (enTelefono ? avisoPendiente === id : condicionDeSiempre);

    return (
        <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1400px] mx-auto space-y-6 animate-fade-in" data-testid="client-dashboard">
            {/* LA CABECERA SE QUEDA como estaba: «Panel del cliente», el saludo y el plan
                contratado. La quité en la primera pasada por ganar los 300 px que ocupa, y
                Francisco la devolvió: «era info que no molestaba». Y es verdad que no
                molesta -- es lo primero que se lee y se lee de un vistazo --, así que lo
                que había que recortar no era esto.

                Lo único que no vuelve es la semana, que aquí salía al lado de la insignia
                del plan y ahora la lleva la tarjeta de Ciclo con su barra. Decirla dos
                veces en la misma pantalla sí sobraba. */}
            <header className="flex items-end justify-between gap-4">
                <div>
                    <p className="caption text-brand mb-1">Panel del cliente</p>
                    <h1 className="font-heading text-4xl md:text-5xl font-bold uppercase text-foreground leading-none">
                        Hola, {user?.name?.split(' ')[0]}
                    </h1>
                    <div className="flex items-center gap-3 mt-2">
                        <PlanBadge plan={profile.plan} planName={myPlan?.name} />
                        {/* La semana, solo en escritorio: en el teléfono la lleva la tarjeta
                            de Ciclo con su barra, y decirla dos veces en la misma pantalla
                            sobra. */}
                        <span className="hidden lg:inline text-muted-foreground text-sm">
                            {cicloSemanas ? `Semana ${profile.week}/${cicloSemanas}` : `Semana ${profile.week}`}
                        </span>
                    </div>
                </div>
                {unreadMessages > 0 && (
                    <button onClick={() => navigate('/dashboard/messages')} data-testid="notif-btn"
                        className="relative w-11 h-11 rounded-xl border border-border bg-card flex items-center justify-center hover:border-brand transition-colors">
                        <Bell className="w-5 h-5 text-foreground" />
                        <span className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 bg-brand text-white text-xs rounded-full flex items-center justify-center font-bold">{unreadMessages}</span>
                    </button>
                )}
            </header>

            {/* Reporte pendiente esta semana (amarillo en plazo, rojo vencido). Solo el
                primero: si le tocan dos, el segundo sale cuando mande el primero. */}
            {sale('reporte', dueReports.length > 0) && (enTelefono ? dueReports.slice(0, 1) : dueReports).map((r) => (
                <div key={r.tipo}
                    className={`surface p-4 flex flex-col sm:flex-row sm:items-center gap-3 border ${
                        r.overdue ? 'border-red-500/40 bg-red-500/5' : 'border-yellow-500/40 bg-yellow-500/5'
                    }`}
                    data-testid={`due-report-${r.tipo}`}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        r.overdue ? 'bg-red-500/15' : 'bg-yellow-500/15'
                    }`}>
                        <AlertTriangle className={`w-5 h-5 ${r.overdue ? 'text-red-500' : 'text-yellow-500'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-foreground font-semibold text-sm">
                            {r.overdue
                                ? `Tu ${r.tipo_label.toLowerCase()} está fuera de plazo`
                                : r.is_open
                                    ? `Tienes tu ${r.tipo_label.toLowerCase()} pendiente`
                                    : `Este fin de semana toca tu ${r.tipo_label.toLowerCase()}`}
                        </p>
                        <p className="text-muted-foreground text-xs">
                            {r.overdue
                                ? 'La ventana de esta semana se cerró; podrás enviarlo la próxima.'
                                : r.is_open
                                    ? `Rellénalo antes del ${r.deadline_label}.`
                                    : `La ventana abre el ${r.opens_label}.`}
                        </p>
                    </div>
                    {r.is_open && (
                        <button onClick={() => navigate('/dashboard/reports')}
                            className="btn-brand flex items-center gap-1.5 text-sm flex-shrink-0 self-start sm:self-auto"
                            data-testid={`due-report-btn-${r.tipo}`}>
                            Hacer reporte <ChevronRight className="w-4 h-4" />
                        </button>
                    )}
                </div>
            ))}

            {/* Checklist de primeros pasos */}
            {sale('checklist', showChecklist) && (
                <OnboardingChecklist steps={checklistSteps} onDismiss={dismissChecklist} navigate={navigate}
                    onResume={resumeTour} showResume={!tourCompleted && !tourActive} />
            )}

            {/* Main grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                {/* Macros / Today */}
                <div className="lg:col-span-8">
                    {hasMacros ? (
                        <div className="surface surface-hover overflow-hidden cursor-pointer h-full" data-testid="macro-trackers-card" onClick={() => navigate('/dashboard/nutrition')}>
                            {/* LA FECHA ARRIBA, y el tipo de día detrás (documento del 10-08,
                                pantalla 4). Antes ponía «Hoy · Entreno», que no sitúa: el
                                cliente que abre la app a las once de la noche o el que navega
                                a otro día necesita ver de qué día son estos números. */}
                            <div className="px-5 pt-5 pb-1">
                                <p className="text-sm font-semibold text-foreground first-letter:uppercase" data-testid="hoy-fecha">
                                    {ahoraDeLaApp().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                                    {/* «DÍA DE ENTRENO», no «entreno» (Francisco, 13-08-2026).
                                        Suelto detrás de la fecha, «entreno» se lee como si
                                        fuera la hora del entrenamiento y no lo que es: el tipo
                                        de día del que salen estos macros. El descanso va igual,
                                        que si no la misma línea dice una cosa de dos maneras. */}
                                    <span className="text-muted-foreground font-normal"> · {isRestDay ? 'día de descanso' : 'día de entreno'}</span>
                                </p>
                            </div>
                            <div className="px-5 pb-5 pt-3">
                                {/* NÚMEROS GRANDES, SIN ANILLO. Del documento: «el anillo dice
                                    lo mismo que la barra, y la barra ocupa una décima parte».
                                    En el teléfono estaban las dos cosas -- tres anillos de
                                    92 px y, debajo, tres barras con los mismos números --,
                                    media pantalla para decir una cosa dos veces.

                                    En el ordenador seguían los anillos, porque el encargo era
                                    no tocar el escritorio. Jesús, 11-08: *«el anillo se ve
                                    bonito y hay que interpretarlo»*. Con el número hay que
                                    hacer una resta, pero al menos está escrito; con el anillo
                                    hay que medir un hueco a ojo.

                                    Ojo, que él compara con el «te queda por comer» del móvil y
                                    eso es de OTRA pantalla: vive en la cabecera de Nutrición
                                    (DayHeader). Aquí, en Inicio, se enseña lo comido sobre el
                                    objetivo, y así es en los dos aparatos. Si hay que cambiar
                                    a «lo que queda», se cambia en Inicio para los dos, y esa
                                    es una decisión aparte -- no parte de igualar el escritorio
                                    con el móvil, que es lo que se está haciendo aquí. */}
                                <div className="grid grid-cols-3 gap-3">
                                    <MacroGrande valor={todayConsumed.P} objetivo={totalDelDia ? totalDelDia.P : getP(activeTarget)} label="Proteína" color={MACRO.protein} />
                                    <MacroGrande valor={todayConsumed.H} objetivo={totalDelDia ? totalDelDia.H : getH(activeTarget)} label="Hidratos" color={MACRO.carbs} />
                                    <MacroGrande valor={todayConsumed.G} objetivo={totalDelDia ? totalDelDia.G : getG(activeTarget)} label="Grasa" color={MACRO.fat} />
                                </div>
                            </div>
                            {/* AQUÍ IBA EL PIE DEL PERIENTRENO, y se ha quitado entero
                                (Francisco, 13-08-2026): «lo de perientreno lo ajustas tú, eso
                                sobra».

                                Eran dos datos que no pintan nada en Inicio. El perientreno ya
                                está sumado en los tres números grandes de arriba, así que
                                repetirlo suelto solo invita a restar. Y el «lo ajustas tú» era
                                el último resto de la palabra cruda del dato («MANUAL»/«AUTO»),
                                que el 11-08 se tradujo a algo legible en vez de quitarse: al
                                cliente le da igual quién le cuadró los macros, y en Inicio no
                                puede hacer nada con esa información.

                                Donde sí vive esto es en Nutrición (DaySummary y DayHeader),
                                que es la pantalla donde se reparten las comidas. */}
                        </div>
                    ) : (
                        <button onClick={() => navigate('/dashboard/macro-calculator')} data-testid="setup-macros-card"
                            className="surface surface-hover w-full p-6 text-center h-full">
                            <Scale className="w-8 h-8 text-brand mx-auto mb-2" />
                            <p className="font-bold text-foreground text-sm uppercase">Configura tus macros</p>
                        </button>
                    )}
                </div>

                {/* La columna de al lado, tal cual estaba. EL CICLO SE QUEDA también en el
                    teléfono: lo quité en la primera pasada por considerarlo repetido con la
                    semana de la cabecera, y Francisco lo devolvió. El número dice en qué
                    semana está; la barra dice cuánto le queda, que es lo que sitúa en un
                    método que se llama 12 en 12. */}
                <div className="lg:col-span-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-5">
                <div className="surface p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="caption">Ciclo</span>
                        <span className="text-xs text-muted-foreground font-data">
                            {cicloSemanas ? `${profile.week}/${cicloSemanas}` : `Semana ${profile.week}`}
                        </span>
                    </div>
                    {cicloSemanas ? (
                        <>
                            <div className="w-full bg-muted rounded-full h-2">
                                <div className="bg-brand h-2 rounded-full transition-all duration-500" style={{ width: `${weekProgress}%` }} />
                            </div>
                            {/* «de tu plan», no «de tu ciclo» (18-09): estas son las semanas que ha PAGADO, y el
                                ciclo es otra cosa, el bloque de cuatro del reporte. Ver MiSemanaPage. */}
                            <p className="text-xs text-muted-foreground mt-2">Semana {profile.week} de tu plan de {plural(cicloSemanas, 'semana')}</p>
                        </>
                    ) : (
                        <p className="text-xs text-muted-foreground mt-1">Plan mensual · semana {profile.week}</p>
                    )}
                    {(myPlan?.habilitaciones?.reportes?.length > 0) && (
                        <p className="text-[11px] text-muted-foreground mt-2 capitalize">
                            Reportes: {myPlan.habilitaciones.reportes.join(' + ')}
                        </p>
                    )}
                </div>

                {/* La próxima renovación, solo en escritorio: en el teléfono es de Mi perfil,
                    que es donde alguien va a mirar qué paga y cuándo. */}
                {profile.next_payment && (
                    <div className="hidden lg:flex surface p-4 items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                            <CreditCard className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div className="min-w-0">
                            <p className="caption">Próxima renovación</p>
                            <p className="text-sm text-foreground mt-0.5">
                                {pintarDia(profile.next_payment, { day: 'numeric', month: 'long' })} · <span className="text-brand font-bold font-data">{profile.price}€</span>
                            </p>
                        </div>
                    </div>
                )}
                </div>
            </div>

            {/* Ajustar macros: el cuestionario del paso 2. Sale mientras tenga los macros
                provisionales del alta, porque hasta que lo rellene sus numeros no estan ajustados.
                En el plan con coach el boton se llama "Rellena tu formulario", que es lo que es:
                el coach lo revisa con el despues. */}
            {/* Aquí se separan los planes: el mismo hueco dice tres cosas distintas.
                El Nivel 3 no rellena nada -- se lo preguntan en la llamada -- así que ni
                se le manda al cuestionario ni se le dice que le falta algo por hacer. */}
            {/* Y SOLO SI DE VERDAD LE FALTA (punto 4.1). `ajuste_macros_completado` es False
                para todo el que no pasó por NUESTRO cuestionario de ajuste, y eso son los 160
                que vinieron de Calma más todo aquel al que el coach le puso los macros a mano:
                medido en producción, 169 de los 174 activos veían este aviso. Gente que lleva
                meses con Jesús, a la que él mismo les pone los números cada quincena, abriendo
                la app y leyendo que les falta terminar de sacar sus macros.
                `macros_puestos_por_alguien` lo calcula el servidor mirando quién escribió su
                último ajuste. */}
            {sale('ajustar', profile?.questionnaire_completed && !profile?.ajuste_macros_completado
                && !profile?.macros_puestos_por_alguien) && (() => {
                // El Premium de antes y el 12EN12, que es el que lo sustituye (24-09).
                const porLlamada = ['nivel3', 'plan_12en12'].includes(profile?.plan || '');
                const conCoach = can('macros_personalizados');
                const texto = porLlamada
                    ? { titulo: 'Agenda tu llamada' }
                    : conCoach
                        ? { titulo: 'Completa tu cuestionario inicial' }
                        : { titulo: 'Termina de ajustar tus macros iniciales' };
                return (
                    <button onClick={() => navigate(porLlamada ? '/dashboard/messages' : '/questionnaire?ajustar=1')}
                        data-testid="ajustar-macros-banner"
                        className="surface surface-hover w-full p-4 flex items-center justify-between group border-2 border-brand/40">
                        <div className="flex items-center gap-4">
                            <div className="w-11 h-11 bg-brand/10 rounded-xl flex items-center justify-center">
                                {porLlamada ? <Phone className="w-5 h-5 text-brand" />
                                            : <SlidersHorizontal className="w-5 h-5 text-brand" />}
                            </div>
                            <div className="text-left">
                                <p className="font-bold text-foreground text-sm uppercase tracking-wide">{texto.titulo}</p>
                            </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-brand transition-colors" />
                    </button>
                );
            })()}

            {/* Sin plan contratado no puede hacer casi nada, y hasta ahora no habia forma de
                llegar a contratarlo desde dentro de la app. Va lo primero a proposito. */}
            {sale('plan', !myPlan) && seVendenPlanes() && (
                <button onClick={() => navigate('/planes')} data-testid="elegir-plan-banner"
                    className="surface surface-hover w-full p-4 flex items-center justify-between group border border-brand/40">
                    <div className="flex items-center gap-4">
                        <div className="w-11 h-11 bg-brand/10 rounded-xl flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-brand" />
                        </div>
                        <div className="text-left">
                            <p className="font-bold text-foreground text-sm uppercase tracking-wide">
                                {planUnpaid ? 'Te quedó un pago a medias' : 'Elige cómo quieres hacerlo'}
                            </p>
                        </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-brand transition-colors" />
                </button>
            )}

            {/* Revisión suelta: solo para quien se autogestiona y ya tiene sus macros ajustados.
                Es la puerta de entrada a tener coach: prueba lo que se siente y, si sube de plan
                en 30 días, lo que pagó se le descuenta.

                Era una tarjeta grande con su flecha. El documento de Jesús del 06-08-2026 dice
                que esto va "como una línea pequeña, sin botón grande", y que nunca interrumpa:
                lo que vende es la pantalla (/dashboard/revision), no el sitio desde el que se
                entra. Los dos momentos de verdad son al recibir los macros de inicio y al
                recibir el ajuste del mes; esto es solo la puerta que queda siempre a mano. */}
            {profile?.ajuste_macros_completado && seLeOfreceLaRevision(profile, can) && (
                <p className="text-xs text-muted-foreground px-1" data-testid="revision-suelta-banner">
                    ¿Prefieres que lo miremos nosotros?{' '}
                    <button onClick={() => navigate('/dashboard/revision')}
                        className="underline text-brand hover:text-brand/80 font-medium">
                        Solicitar ajuste personalizado
                    </button>.
                </p>
            )}

            {/* Ya la pagó: que sepa en qué punto está. */}
            {ajusteEnManosDelEquipo(profile) && (
                <div className="surface w-full p-4 flex items-center gap-4 border border-brand/30">
                    <div className="w-11 h-11 bg-brand/10 rounded-xl flex items-center justify-center">
                        <ClipboardCheck className="w-5 h-5 text-brand" />
                    </div>
                    <div>
                        <p className="font-bold text-foreground text-sm uppercase tracking-wide">Revisión en marcha</p>
                        <p className="text-muted-foreground text-sm">
                            Estamos revisando tus macros. Te avisamos en cuanto estén.
                        </p>
                    </div>
                </div>
            )}

            {/* Cuestionario Nivel 1 pendiente (planes con coach): retomable, no bloqueante.
                UN AVISO CADA VEZ, NO DOS (punto 17). Antes este salía a la vez que el de
                arriba, así que el cliente veía «Completa tu cuestionario inicial» y
                «Completa tu perfil para tu coach» uno debajo del otro y parecía que había
                dos cuestionarios. No los hay: es el mismo recorrido, y el de arriba sigue
                de largo hasta aquí sin cortes. Este solo tiene sentido cuando el de arriba
                ya está hecho -- es decir, cuando de verdad se quedó a medias. */}
            {/* Y también para quien compró el ajuste a medida: hace el completo igual que un
                Gold (doc del cuestionario, 18-08), así que la tarjeta que le lleva a
                terminarlo tiene que salirle a él también. */}
            {/* Y NO A QUIEN YA TIENE QUIEN SE LOS PONGA (30-08). Este aviso promete
                «terminamos de ajustar tus macros», y a un cliente de años cuyos macros
                escribe Jesús no le queda nada que terminar: nunca hizo ese cuestionario
                porque no le hacía falta. Medido en producción: le salía a 99 clientes, 82
                de ellos con la suscripción al día -- gente de gold, nivel3 y calma12 --, y
                en el teléfono es el ÚNICO aviso que se pinta, así que era lo primero que
                veían al abrir la app. Es el caso B del documento de Jesús, y el test que lo
                vigilaba estaba en verde por casualidad: cogía a un cliente caducado.
                La excepción se queda: quien compró el ajuste a medida hace el completo
                igual que un Gold, tenga o no macros puestos por alguien. */}
            {sale('perfil', leQuedaLaSegundaParte(profile, can('macros_personalizados'))
                && (!profile?.macros_puestos_por_alguien || profile?.ajuste_a_medida?.cobrado)) && (
                <button onClick={() => navigate('/questionnaire')} data-testid="nivel1-pending-banner"
                    className="surface surface-hover w-full p-4 flex items-center justify-between group border-2 border-brand/40">
                    <div className="flex items-center gap-4">
                        <div className="w-11 h-11 bg-brand/10 rounded-xl flex items-center justify-center">
                            <ClipboardCheck className="w-5 h-5 text-brand" />
                        </div>
                        <div className="text-left">
                            {/* NI «COACH» NI «ENTRENADOR»: HABLAMOS EN PLURAL.
                                El 4.18 cambió «coach» por «entrenador»; el repaso del 11-08 va
                                un paso más allá y quita los dos de todo lo que lee el cliente.
                                Y de paso el aviso dice qué gana él, no a quién le llega. */}
                            {/* «TERMINAMOS DE AJUSTAR», no «te afinamos» (Francisco, 13-08).
                                Afinar suena a retoque fino de algo que ya está bien; lo que
                                pasa de verdad es que los macros están a medias hasta que
                                conteste, y la frase tiene que decir eso. */}
                            <p className="font-bold text-foreground text-sm uppercase tracking-wide">Completa tu perfil y terminamos de ajustar tus macros</p>
                        </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-brand transition-colors" />
                </button>
            )}

            {/* Today routine highlight (solo si el plan incluye rutina) */}
            {can('rutina') && (
            <button onClick={() => navigate('/dashboard/routine')} data-testid="routine-card"
                className="surface surface-hover w-full p-5 flex items-center justify-between group">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-brand/10 rounded-xl flex items-center justify-center group-hover:bg-brand/15 transition-colors">
                        <Dumbbell className="w-6 h-6 text-brand" />
                    </div>
                    <div className="text-left">
                        <p className="font-bold text-foreground text-sm uppercase tracking-wide">Entreno de hoy · <span className="capitalize font-medium text-muted-foreground">{ahoraDeLaApp().toLocaleDateString('es-ES', { weekday: 'long' })}</span></p>
                        {todayRoutine ? (
                            todayRoutine.is_rest
                                ? <p className="text-muted-foreground text-sm">Día de descanso activo</p>
                                : <p className="text-muted-foreground text-sm">{plural(todayRoutine.exercises?.length || 0, 'ejercicio')} programados</p>
                        ) : hayPdfRutina
                            /* Con rutina subida no hay «sin rutina» que valga: la tarjeta ya
                               lleva a /dashboard/routine, donde se abre. Y sin decir en qué
                               fichero viene, que eso es cosa nuestra (27-08). */
                            ? <p className="text-muted-foreground text-sm">Ya tienes tu rutina. Entra a verla</p>
                            : <p className="text-muted-foreground text-sm">Sin rutina asignada</p>}
                    </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-brand transition-colors" />
            </button>
            )}

            {/* AQUÍ IBAN LOS OCHO ACCESOS RÁPIDOS.
                «Repetían la barra de abajo. Eran cuatro cosas que ocupaban media pantalla y
                no llevaban a ningún sitio nuevo» (documento del 10-08, pantalla 4). En el
                teléfono eran ocho, con icono, título y subtítulo, y se llevaban 1.000 de los
                1.616 px de la pantalla, así que se quitaron.

                En el ordenador se quedaron, porque el encargo era no tocar el escritorio, y
                ahí duplicaban la barra lateral entera: Nutrición, Macros, Asistente,
                Reportes, Alimentos, Suplementos, Check-ins y Chat ya están a la izquierda y
                siempre a la vista, que es más de lo que tiene el móvil (Jesús, 11-08). No se
                pierde ninguna puerta, se deja de tener dos veces la misma. */}
        </div>
    );
};

// =============== NAV CONFIG ===============

// `cap`: capacidad del plan requerida para ver el ítem (ver lib/planAccess.js).
// Sin `cap` = siempre visible.
const NAV_ITEMS = [
    { path: '/dashboard', icon: Sun, label: 'Mi día', end: true },
    // Nutrición antes que Rutina (15-10), el mismo orden que la barra de abajo del móvil.
    { path: '/dashboard/nutrition', icon: Apple, label: 'Nutrición' },
    // Seguimiento sube al cuarto puesto y Mi rutina pasa al quinto (25-10), el mismo
    // orden que la barra de abajo del móvil. Seguimiento y el cierre del día van con la
    // llave del cierre (24-08), no con la de reportes: dentro de Seguimiento el que no
    // vende reportes ve su historial, su Evolución y su Diario, y no ve el formulario.
    // Ver CAP.CIERRE_DIA en lib/planAccess.js. El rótulo «Seguimiento» se pone en navItems.
    { path: '/dashboard/reports', icon: FileText, label: 'Reportes', cap: 'cierre_dia' },
    { path: '/dashboard/routine', icon: Dumbbell, label: 'Mi rutina', cap: 'pestana_rutina' },
    { path: '/dashboard/foods', icon: Search, label: 'Alimentos' },
    // «MIS MACROS» EN TODOS LOS PLANES (tarea 7.3 del 21-08, hueco 1c del doc de Jesús):
    // el rótulo del menú es el nombre de la pantalla, y la pantalla la tiene todo el
    // mundo -- el de autogestión con su calculadora, el de coach en solo lectura --.
    // Lo que cambia por plan se decide DENTRO (macros_ajustables y ventana_revision),
    // no escondiendo la entrada.
    { path: '/dashboard/macro-calculator', icon: SlidersHorizontal, label: 'Mis macros' },
    // «Ranking» sale del menú (14-10, rediseño del cliente); la pantalla sigue en su ruta.
    { path: '/dashboard/supplements', icon: Pill, label: 'Mis suplementos', cap: 'suplementacion' },
    { path: '/dashboard/chatbot', icon: Bot, label: 'Asistente IA' },
    { path: '/dashboard/checkins', icon: ClipboardCheck, label: 'Check-ins', cap: 'cierre_dia' },
    // EL CHAT LO VE TODO EL MUNDO (Francisco, 5-09). Aquí llevaba `cap: 'chat'` por la tabla
    // 20 del doc del 09-08: al plan «solo app» no se le enseña el Chat para no prometerle un
    // entrenador que no tiene. Pero con eso se le quitaban también las dos puertas que no
    // prometen a nadie -- «Mi suscripción» y «Algo no funciona» --, y son 112 clientes de 239
    // sin forma de avisar de un cobro o de que la app no carga. Lo que se cierra es «Mi plan»,
    // dentro de la pantalla y en el servidor; y arriba se dice quién le contesta.
    { path: '/dashboard/messages', icon: MessageCircle, label: 'Chat' },
    { path: '/dashboard/profile', icon: User, label: 'Mi perfil' },
];

// LAS CUATRO DE ABAJO (documento del 10-08): Inicio · Nutrición · Seguimiento · Perfil.
//
// Antes eran tres más un botón «Más» que abría un cajón con las once. Dos problemas: el
// cajón escondía media app detrás de una palabra que no dice nada, y «Macros» ocupaba un
// hueco de cuatro para una pantalla a la que se entra una vez al mes.
//
// «Seguimiento» en vez de «Reportes» es del documento, y es la unificación de las cuatro
// vías de hoy (reportes, check-ins, fotos e informe). Mientras esas cuatro no se junten de
// verdad -- pantallas 20 a 24 del recorrido -- apunta a Reportes, que es donde está lo
// principal, y las otras siguen alcanzables desde Perfil.
//
// Todo lo que sale de aquí tiene su entrada en Perfil. Ninguna pantalla se queda sin puerta.
const BOTTOM_ITEMS = [
    { path: '/dashboard', icon: Sun, label: 'Mi día', end: true },
    { path: '/dashboard/nutrition', icon: Apple, label: 'Nutrición' },
    { path: '/dashboard/reports', icon: TrendingUp, label: 'Seguimiento', cap: 'cierre_dia' },
    { path: '/dashboard/profile', icon: User, label: 'Perfil' },
];

const SidebarLink = ({ item, collapsed, unread, fichaPendiente, onClick }) => (
    <NavLink to={item.path} end={item.end} onClick={onClick}
        title={collapsed ? item.label : undefined}
        className={({ isActive }) => `relative flex items-center gap-3.5 rounded-xl font-semibold transition-all ${collapsed ? 'justify-center px-0 py-3' : 'px-4 py-3'} ${isActive ? 'bg-brand text-white' : 'text-white/55 hover:text-white hover:bg-white/[0.07]'}`}
        data-testid={`nav-${slug(item.label)}`}>
        <span className="relative flex-shrink-0">
            {item.avatar || <item.icon className="w-5 h-5" strokeWidth={2} />}
            {item.path.includes('messages') && unread > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-4 h-4 px-1 bg-brand text-white text-[10px] rounded-full flex items-center justify-center font-bold border border-ink">{unread}</span>
            )}
            {/* EL PUNTO DE «TIENES ALGO PENDIENTE EN TU FICHA» (punto 111 del artifact del
                25-08). El aviso de macros provisionales vivía en la pantalla de comer y
                ocupaba tres líneas: «es un problema de ficha metido en la pantalla de
                comer». Se ha ido a Mi perfil, que es donde se arregla, y aquí queda esto
                para que se vea que hay algo que hacer sin tener que entrar a mirar. */}
            {item.path.includes('profile') && fichaPendiente && (
                <span data-testid="punto-ficha-pendiente"
                    className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-pasado rounded-full border border-ink" />
            )}
        </span>
        {!collapsed && <span className="text-[15px]">{item.label}</span>}
    </NavLink>
);

// =============== CLIENT LAYOUT ===============

const ClientLayout = () => {
    const { user, logout, profile, perfilNoCargado, api, can, planUnpaid, myPlan, pantalla, refreshProfile, loading } = useAuth();
    const donde = useLocation();

    // SIN PLAN, SOLO EL INICIO (Francisco, 29-08): «una persona que no tiene plan no puede
    // utilizar el sistema, solo puede seleccionar un plan para volver a recuperar acceso».
    //
    // El bloqueo vivía únicamente en el Inicio, así que escribiendo /dashboard/nutrition se
    // entraba igual. Aquí se cierra el layout entero: cualquier pantalla que no sea el
    // Inicio devuelve al Inicio, que es donde está «Seleccionar plan». El servidor lo cierra
    // por su lado con un 402 (`core/candado_de_plan`), pero eso llega DESPUÉS de pintar la
    // pantalla; esto evita que llegue a verse.
    //
    // Al equipo no se le aplica: no compra planes. Y `perfilNoCargado` tampoco entra, que es
    // la diferencia entre «no tiene plan» y «no se pudo leer su perfil» (un 500 dejaba al
    // cliente navegando como si nunca hubiera contratado nada, QA del 15-08).
    //
    // El `return` va abajo del todo, con el resto de los hooks ya declarados: cortar aquí
    // arriba deja los de más abajo sin ejecutar y React no lo permite.
    // SIN PERFIL TAMBIÉN ES SIN ACCESO, y ese era el agujero de la primera versión: exigía
    // `profile` para bloquear, y una cuenta RECIÉN CREADA no tiene ficha todavía -- llega
    // `null` --, así que al que se acababa de registrar no se le paraba nada. Lo probó
    // Francisco: «cree una cuenta, me logueo y salgo en la pantalla de planes, pero puedo
    // presionar el botón de volver, y por url ir al inicio del sistema».
    //
    // `loading` es lo que evita el falso positivo: mientras se lee el perfil, `profile`
    // también es null, y sin esperar se echaría del sitio a quien sí tiene plan cada vez
    // que recarga. Y `perfilNoCargado` distingue «no tiene ficha» de «no se pudo leer»
    // (un 500 dejaba al cliente navegando como si nunca hubiera contratado nada, QA 15-08).
    const esDelEquipo = user?.role === 'admin' || user?.role === 'trainer';
    // La regla, en `lib/puertasDelCliente` (12-10): sin acceso se abren el Inicio y Mi
    // perfil, que es justo lo que le queda en el menu. Antes solo el Inicio, y "Mi perfil"
    // era una puerta pintada: se pulsaba y volvia al Inicio.
    const sinAcceso = sinAccesoALaApp({ loading, esDelEquipo, perfilNoCargado, profile });
    const fueraDelInicio = !puedeAbrir(donde.pathname, sinAcceso);

    // ALGO PENDIENTE EN LA FICHA (punto 111): datos que faltan o que no pueden ser
    // (edad 5, estatura de 1 cm de la importación de Calma). Lo calcula el servidor al
    // leer el perfil (core/datos_dudosos.py) y ya viajaba en `profile`; aquí solo se usa
    // para encender el punto del menú, que es lo que sustituye al aviso de Nutrición.
    // APAGADO (Enrique, 29-10): se quitó el aviso «Macros provisionales» de Mi perfil, y el
    // punto de «Mi perfil» ya no avisa de nada. Se deja la variable para no tocar los menús.
    const fichaPendiente = false;

    // AVISO DE MODO PRUEBAS: si el que prueba ha puesto su cuenta en un estado ficticio
    // (caducado, sin plan...), puede quedarse sin forma de volver desde esa misma pantalla.
    // Este botón flotante lo restaura desde cualquier sitio. Solo para cuentas es_pruebas.
    const restaurarPruebas = async () => {
        try {
            await api.post('/settings/mis-pruebas/restaurar');
            await refreshProfile();
        } catch (e) { /* silencioso: es una herramienta interna */ }
    };
    // «MIS MACROS» PARA TODOS (tarea 7.3 del 21-08). El doc 19-08 la había quitado al de
    // coach («un papel colgado en la pared»); el hueco 1c del doc de Jesús lo revierte:
    // la pestaña existe en todos los planes -- en solo lectura para quien no edita -- y
    // dentro viven sus tres bloques (los de ahora, cambiarlos tú, el botón Revisar).
    // «SEGUIMIENTO» ES LA ÚNICA PUERTA, EN EL TELÉFONO Y EN EL ORDENADOR.
    //
    // El menú tenía «Reportes» y «Check-ins» como dos entradas distintas, y el cliente tenía
    // que saber de antemano en cuál de las dos está lo que busca. Las dos se sustituyen por
    // una sola, «Seguimiento», que es la portada desde la que se entra a cada cosa: la
    // revisión del mes, el check-in de hoy, la evolución y lo ya mandado.
    //
    // Esto se hizo el 10-08 solo en el teléfono, porque el encargo era no tocar el
    // escritorio. Jesús, 11-08: *«en el teléfono hay nueve entradas y una se llama
    // Seguimiento; en el ordenador hay once, y ahí siguen Reportes y Check-ins por
    // separado»*. La misma persona abre la app en el móvil por la mañana y en el ordenador
    // por la tarde: si la sección se llama distinto en cada sitio, la busca dos veces. Una
    // sola lista para los dos.
    //
    // La ruta /dashboard/checkins sigue viva: lo que desaparece es la entrada del menú, no
    // la pantalla, y a ella se entra desde Seguimiento.
    // EL ASISTENTE, ESCONDIDO PARA TODOS (Francisco, 26-08). Detrás de un interruptor del
    // panel que nace APAGADO: sin fila en `pantallas`, `pantalla()` devuelve el valor por
    // defecto, así que basta con esto para que deje de verse hoy mismo. Se enciende desde
    // el panel el día que se quiera, sin volver a desplegar. La pantalla sigue existiendo
    // (`ChatbotPage`) y el backend intacto: lo que se quita es la puerta.
    const asistenteVisible = pantalla('t7_asistente');
    // SIN PLAN, EL MENÚ SE QUEDA EN EL INICIO Y SU PERFIL. Con el bloqueo puesto seguían
    // saliendo Nutrición, Alimentos y Mis macros: puertas que ya no abren -- se entra y
    // rebota al Inicio --, y enseñarlas es prometerle algo que no tiene. Su perfil se queda
    // porque ahí están sus datos y el cierre de sesión (29-08).
    const navItems = (sinAcceso
            ? NAV_ITEMS.filter(i => ABIERTO_SIN_ACCESO.includes(i.path))
            : NAV_ITEMS)
        .filter(i => (!i.cap || can(i.cap)) && i.path !== '/dashboard/checkins')
        .filter(i => i.path !== '/dashboard/chatbot' || asistenteVisible)
        .map(i => i.path === '/dashboard/reports'
            ? { ...i, icon: TrendingUp, label: 'Seguimiento' }
            : i)
        // «MI SEMANA» TAMBIÉN EN EL ORDENADOR: la página nació el 21-08 con su única
        // puerta en la barra de abajo del móvil, y en escritorio solo se llegaba por
        // URL. Entra en el menú lateral entre Inicio y Rutina -- el orden de la barra
        // del móvil -- y detrás del MISMO interruptor que ella (t1_inicio_nuevo), para
        // que el lunes se encienda todo junto. En el móvil no duplica nada: la hoja de
        // «Más» descarta las rutas que ya están en la barra.
        .flatMap(i => (i.path === '/dashboard' && pantalla('t1_inicio_nuevo') && !sinAcceso)
            ? [i, { path: '/dashboard/semana', icon: CalendarRange, label: 'Mi semana' }]
            : [i]);
    // La barra de abajo no se toca: ahí ya pone «Macros» a secas, que vale para los dos casos y
    // es lo que cabe en un móvil.
    // Lo mismo en la barra de abajo del móvil, que es el menú de verdad en un teléfono.
    const bottomItems = (sinAcceso ? BOTTOM_ITEMS.filter(i => i.path === '/dashboard') : BOTTOM_ITEMS)
        .filter(i => !i.cap || can(i.cap));
    const navigate = useNavigate();
    const location = useLocation();
    const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebar-collapsed') === '1');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [unread, setUnread] = useState(0);

    // Campanita: novedades del coach (rutina, macros, feedback, suplementos, coach)
    const [notifCount, setNotifCount] = useState(0);
    const [notifOpen, setNotifOpen] = useState(false);
    const [notifItems, setNotifItems] = useState([]);
    // Si ya se preguntó o se está preguntando. El panel se abre antes de que lleguen los
    // avisos, y sin esto la primera vez decía «No hay nada pendiente» encima de una lista
    // que estaba llegando: al segundo intento aparecían. Decirle que no tiene nada cuando
    // todavía no se sabe es lo mismo que mentirle.
    const [notifCargando, setNotifCargando] = useState(false);

    const pedirContadores = useCallback(() => {
        api.get('/messages/unread-count?vista=cliente').then(r => setUnread(r.data.count || 0)).catch(() => {});
        api.get('/notifications/unread-count').then(r => setNotifCount(r.data.count || 0)).catch(() => {});
    }, [api]);
    useEffect(() => { pedirContadores(); }, [pedirContadores, location.pathname]);
    // Y SIN CAMBIAR DE PANTALLA (12-10): al leer en el chat, cada minuto con la pestaña a la
    // vista y al volver a ella (`lib/contadoresDelCliente`). Antes solo se pedían al navegar.
    useEffect(() => vigilarContadores(pedirContadores), [pedirContadores]);

    const openNotifications = async () => {
        setNotifOpen(true);
        setNotifCargando(true);
        try {
            const res = await api.get('/notifications');
            // ABRIR NO MARCA NADA (26-10, Francisco: «como el correo»). Cada aviso sigue sin leer
            // hasta que lo pulsa (`atenderAviso`), y el globo baja con cada uno.
            setNotifItems(res.data.notifications || []);
        } catch { /* silencioso */ } finally {
            setNotifCargando(false);
        }
    };

    // Cuestionario inicial: tras contratar un plan (perfil activo), si no lo ha
    // completado, forzar el quiz para calcular sus macros. Mientras el pago esté
    // pendiente (status != 'activo') no se fuerza.
    // Y SOLO A QUIEN TIENE ACCESO (12-10): al que nace con el acceso vencido el
    // cuestionario le contesta 402, el 402 lo devuelve aqui y esto lo volvia a mandar al
    // cuestionario, en bucle y con la pantalla en blanco. La regla esta en
    // `lib/puertasDelCliente`: sin acceso se queda en el Inicio, que le dice que su
    // suscripcion ha terminado.
    // SALVO DESDE MENSAJES Y MI PERFIL (12-10, Francisco): quien ya pagó puede escribir al
    // equipo sin hacer antes el cuestionario. Qué pantallas, en la misma regla.
    useEffect(() => {
        if (leTocaElCuestionario(profile, sinAcceso, donde.pathname)) {
            navigate('/questionnaire', { replace: true });
        }
    }, [profile, sinAcceso, navigate, donde.pathname]);

    useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

    const toggleCollapsed = useCallback(() => {
        setCollapsed(c => { localStorage.setItem('sidebar-collapsed', !c ? '1' : '0'); return !c; });
    }, []);

    const handleLogout = () => { logout(); navigate('/auth'); toast.success('Sesión cerrada'); };
    const isStaff = ['admin', 'trainer'].includes(user?.role);

    // EL MENÚ EN DOS GRUPOS (rediseño del 14-10): arriba las pantallas de trabajo y abajo,
    // tras una raya, lo de la cuenta -- Chat, Mi perfil (con su foto o su inicial, que es
    // lo que antes decía la ficha del nombre) y salir --. Mismas entradas que antes.
    // EL CHAT SALE DEL MENÚ (14-10): es el botón redondo de abajo a la derecha, en todas las
    // pantallas (ver `chatFlotante`).
    const RUTA_DEL_CHAT = '/dashboard/messages';
    const hayChat = navItems.some(i => i.path === RUTA_DEL_CHAT);
    const ABAJO_DEL_MENU = ['/dashboard/profile'];
    const avatarDelPerfil = (
        <span className="w-6 h-6 rounded-full bg-brand/15 border border-brand/40 flex items-center justify-center overflow-hidden">
            {urlDeFoto(user)
                ? <img src={urlDeFoto(user)} alt="" className="w-full h-full object-cover" />
                : <span className="text-brand font-bold font-heading text-xs">{user?.name?.charAt(0)?.toUpperCase()}</span>}
        </span>
    );
    const navArriba = navItems.filter(i => !ABAJO_DEL_MENU.includes(i.path) && i.path !== RUTA_DEL_CHAT);
    const navAbajo = navItems.filter(i => ABAJO_DEL_MENU.includes(i.path))
        .map(i => i.path === '/dashboard/profile' ? { ...i, avatar: avatarDelPerfil } : i);
    const claseDeSalir = (plegado) => `flex items-center gap-3.5 w-full rounded-xl font-semibold text-white/55 hover:text-red-400 hover:bg-red-500/10 transition-colors ${plegado ? 'justify-center py-3' : 'px-4 py-3'}`;

    // SIN PLAN, SOLO EL INICIO. Aquí, con todos los hooks ya declarados (ver arriba).
    if (fueraDelInicio) return <Navigate to="/dashboard" replace />;

    // AL PULSAR EL AVISO, SE QUEDA EN LA CAMPANA, LEÍDO (26-10). Del 22-10 al 26-10 se quitaba
    // de la lista; ahora la campana es una bandeja de correo y el aviso sigue ahí, apagado.
    // El servidor lo apunta como atendido y leído (`PUT /notifications/{id}/atendido`). Es lo
    // ÚNICO que lo marca leído (abrir la campana no), así que se puede pulsar aunque no lleve
    // a ninguna pantalla: sin eso, un aviso sin enlace se quedaría sin leer para siempre.
    const atenderAviso = (n) => {
        setNotifItems(prev => prev.map(x => (x.id === n.id ? { ...x, read: true } : x)));
        if (!n.read) setNotifCount(c => Math.max(0, c - 1));
        api.put(`/notifications/${n.id}/atendido`).catch(() => {});
        if (n.link) {
            setNotifOpen(false);
            navigate(n.link);
        }
    };

    /** LOS AVISOS COMO UNA BANDEJA DE CORREO (26-10). Los diez últimos, en columnas: Leído
     *  (check o punto), Fecha («26-OCT») y Aviso (el título). Los sin leer, con fondo y raya
     *  de color y en negrita; los leídos, apagados. TODA LA FILA SE PULSA y lleva a su
     *  pantalla (sin columna de enlace); el aviso parte en dos líneas si hace falta.
     *  Mismas columnas en la cabecera y en cada fila: `COLUMNAS_AVISOS`. */
    const COLUMNAS_AVISOS = 'grid grid-cols-[1.25rem_3.25rem_minmax(0,1fr)] sm:grid-cols-[3rem_3.75rem_minmax(0,1fr)] items-center gap-x-2 sm:gap-x-3';
    const renderAviso = (n) => {
        return (
            <button key={n.id} type="button" data-testid={`aviso-${n.id}`} data-leido={n.read ? 'si' : 'no'}
                onClick={() => atenderAviso(n)} {...(n.link ? { title: n.salida || 'Ver' } : {})}
                className={`${COLUMNAS_AVISOS} w-full text-left pl-3 pr-3 sm:pl-4 py-3 border-b border-border last:border-0 border-l-[3px] transition-colors cursor-pointer hover:bg-muted ${n.read
                    ? 'border-l-transparent opacity-60'
                    : 'border-l-brand bg-brand/[0.07]'}`}>
                <span className="flex justify-center" role="img" aria-label={n.read ? 'Leído' : 'Sin leer'}>
                    {n.read
                        ? <Check className="w-4 h-4 text-muted-foreground" strokeWidth={2.5} />
                        : <span className="w-2.5 h-2.5 rounded-full bg-brand" />}
                </span>
                <span className={`text-xs tabular-nums whitespace-nowrap ${n.read ? 'text-muted-foreground' : 'text-brand font-bold'}`}>
                    {fechaCorta(n.created_at)}
                </span>
                <span className={`text-sm leading-snug line-clamp-2 ${n.read ? 'text-muted-foreground' : 'text-foreground font-semibold'}`}>
                    {n.title}
                </span>
            </button>
        );
    };

    return (
        <div className="min-h-screen bg-background flex">
            <FraseAlEntrar />
            {/* Aviso flotante de modo pruebas: solo para cuentas es_pruebas con un escenario puesto. */}
            {user?.es_pruebas && profile?.pruebas_escenario && (
                <div className="fixed bottom-24 lg:bottom-6 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-3 rounded-full border border-yellow-500/50 bg-yellow-500/15 backdrop-blur px-4 py-2 shadow-lg">
                    <span className="text-xs font-semibold text-yellow-200">Cuenta en modo prueba: {profile.pruebas_escenario}</span>
                    <button onClick={restaurarPruebas} className="text-xs font-bold rounded-full bg-yellow-500 text-black px-3 py-1 hover:bg-yellow-400 transition-colors">
                        Restaurar
                    </button>
                </div>
            )}
            {/* ===== Desktop sidebar ===== */}
            <aside className={`hidden lg:flex flex-col bg-ink h-screen sticky top-0 flex-shrink-0 transition-[width] duration-300 ${collapsed ? 'w-[78px]' : 'w-64'}`} data-testid="desktop-sidebar">
                <div className={`flex items-center h-20 ${collapsed ? 'justify-center px-0' : 'justify-between pl-6 pr-4'}`}>
                    {!collapsed && <Logo12EN12 size="md" tone="dark" />}
                    <button onClick={toggleCollapsed} data-testid="sidebar-toggle"
                        className="w-9 h-9 rounded-lg flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors">
                        {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
                    </button>
                </div>
                <nav className="flex-1 overflow-y-auto no-scrollbar px-3 py-2 space-y-1.5">
                    {navArriba.map(item => <SidebarLink key={item.path} item={item} collapsed={collapsed} unread={unread} fichaPendiente={fichaPendiente} />)}
                </nav>
                <div className="mx-3 pt-3 pb-4 border-t border-white/10 space-y-1.5">
                    {/* AVISOS, ABAJO Y JUSTO ENCIMA DEL CHAT (14-10). «AVISOS», NO «NOVEDADES» (Jesús, 18-08). Dentro de la app todo esto
                        son avisos -- así se llaman en el doc del 16-08 y así se los nombra
                        él -- y el rótulo era lo único que seguía diciendo otra cosa. */}
                    <button onClick={openNotifications} data-testid="client-bell-desktop"
                        title={collapsed ? 'Avisos' : undefined}
                        className={`relative flex items-center gap-3.5 rounded-xl w-full font-semibold transition-all text-white/55 hover:text-white hover:bg-white/[0.07] ${collapsed ? 'justify-center px-0 py-3' : 'px-4 py-3'}`}>
                        <span className="relative flex-shrink-0">
                            <Bell className="w-5 h-5" strokeWidth={2} />
                            {(notifCount + unread) > 0 && (
                                <span className="absolute -top-1.5 -right-1.5 min-w-4 h-4 px-1 bg-brand text-white text-[10px] rounded-full flex items-center justify-center font-bold border border-ink">{notifCount + unread}</span>
                            )}
                        </span>
                        {!collapsed && <span className="text-[15px]">Avisos</span>}
                    </button>
                    {navAbajo.map(item => <SidebarLink key={item.path} item={item} collapsed={collapsed} unread={unread} fichaPendiente={fichaPendiente} />)}
                    {isStaff && (
                        <button onClick={() => navigate('/admin')} data-testid="go-admin-btn"
                            className={`flex items-center gap-3.5 w-full rounded-xl font-semibold text-white/55 hover:text-brand hover:bg-brand/10 transition-colors ${collapsed ? 'justify-center py-3' : 'px-4 py-3'}`}>
                            <LayoutDashboard className="w-5 h-5" /> {!collapsed && <span className="text-[15px]">Volver al panel</span>}
                        </button>
                    )}
                    <button onClick={handleLogout} data-testid="logout-btn" className={claseDeSalir(collapsed)}>
                        <LogOut className="w-5 h-5" /> {!collapsed && <span className="text-[15px]">Cerrar sesión</span>}
                    </button>
                </div>
            </aside>

            {/* ===== Main ===== */}
            <div className="flex-1 min-w-0 flex flex-col">
                {/* Mobile top bar */}
                {/* EL LOGO, CENTRADO DE VERDAD. Con `justify-between` el logo se coloca entre
                    lo que tiene a los lados, y a la izquierda hay un botón y a la derecha
                    dos: quedaba desplazado a la izquierda. Se centra sobre la barra entera y
                    los dos grupos de botones se quedan en sus esquinas. */}
                {/* EL CHAT Y LOS AVISOS, ARRIBA EN TODAS LAS PANTALLAS (handoff de Nutrición; Enrique,
                    16-10: «mover chat y alertas a la cabecera de forma global»). El botón flotante del
                    chat tapaba los − / + y la papelera justo donde más se toca. A la derecha van el
                    chat, la campana y el hueco del «···» de la pantalla que lo quiera
                    (`hueco-menu-pantalla`: Nutrición monta ahí el suyo). Con tres botones a la
                    derecha el logo ya no cabe centrado a 320 px, así que va a la izquierda. */}
                <header className="lg:hidden sticky top-0 z-40 bg-ink h-14 flex items-center justify-between gap-2 px-4">
                    {/* LAS TRES BARRITAS, SOLO CON LA BARRA VIEJA (15-10). Con la de abajo nueva,
                        su «Más» ya tiene todo el menú: eran dos caminos a lo mismo, y el de arriba
                        a la izquierda es el peor sitio para el pulgar. El hueco se queda para que
                        el logo siga centrado. */}
                    <div className="flex items-center gap-1 min-w-0">
                        {!pantalla('t1_inicio_nuevo') && (
                        <button onClick={() => setDrawerOpen(true)} data-testid="mobile-menu-btn"
                            className="w-10 h-10 -ml-2 rounded-lg flex items-center justify-center text-white/80 hover:bg-white/10">
                            <Menu className="w-6 h-6" />
                        </button>
                        )}
                        <Logo12EN12 size="sm" tone="dark" />
                    </div>
                    <div className="flex items-center gap-1.5 -mr-1 flex-shrink-0">
                        {hayChat && (
                            <button type="button" onClick={() => navigate(RUTA_DEL_CHAT)}
                                data-testid="chat-cabecera" aria-label={unread > 0 ? `Chat, ${unread} sin leer` : 'Chat'} title="Chat"
                                aria-current={location.pathname.startsWith(RUTA_DEL_CHAT) ? 'page' : undefined}
                                className={`relative w-9 h-9 rounded-[10px] flex items-center justify-center transition-colors ${
                                    location.pathname.startsWith(RUTA_DEL_CHAT) ? 'bg-brand text-white' : 'bg-white/10 text-white/80 hover:text-white'}`}>
                                <MessageCircle className="w-5 h-5" strokeWidth={2.2} />
                                {unread > 0 && (
                                    <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-brand text-white text-[10px] font-bold flex items-center justify-center border border-ink">
                                        {unread}
                                    </span>
                                )}
                            </button>
                        )}
                        <button onClick={openNotifications} data-testid="client-bell" aria-label="Avisos" title="Avisos"
                            className="relative w-9 h-9 rounded-[10px] bg-white/10 flex items-center justify-center text-white/80 hover:text-white">
                            <Bell className="w-5 h-5" />
                            {/* Los mensajes sin leer ya los cuenta el chat, al lado; si no hay chat,
                                la campana los sigue sumando como antes. */}
                            {(notifCount + (hayChat ? 0 : unread)) > 0 && <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-brand rounded-full" />}
                        </button>
                        <span id="hueco-menu-pantalla" className="contents" />
                    </div>
                </header>

                {/* Abajo se deja sitio para la barra de pestañas del teléfono y, en el ordenador,
                    para el botón del chat, que flota encima del final de cada pantalla. En el
                    teléfono el chat ya no flota (va en la cabecera, 16-10): basta la barra. */}
                <main className="flex-1 pb-20 lg:pb-24">
                    {/* Si el perfil no ha podido cargarse, DECIRLO. Sin esto la app se
                        comporta como si el cliente no tuviera plan -- sin macros, sin
                        acceso -- y él no tiene forma de saber que lo que ve es un fallo
                        y no su cuenta. */}
                    {perfilNoCargado && (
                        <div data-testid="aviso-perfil-no-cargado"
                            className="mx-4 mt-4 rounded-lg border border-amber-400/40 bg-amber-50 dark:bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-200">
                            No hemos podido cargar tus datos ahora mismo, así que puede que
                            veas la app incompleta. Vuelve a entrar en un momento y, si sigue
                            igual, escríbenos.
                        </div>
                    )}
                    {/* Igual que en el panel del coach: un fallo de una pantalla no puede
                        dejar al cliente sin app. Ver LimiteDeError. */}
                    <LimiteDeError clave={location.pathname + location.search}>
                        <Outlet />
                    </LimiteDeError>
                </main>
            </div>

            {/* ===== El chat, flotante (14-10) =====
                Abajo a la derecha: libre en escritorio (a la izquierda está el menú) y donde
                llega el pulgar en el teléfono. Ahí va por encima de la barra de abajo. No sale
                en la propia pantalla del chat, donde taparía el cuadro de escribir. */}
            {hayChat && !location.pathname.startsWith(RUTA_DEL_CHAT) && (
                <button type="button" onClick={() => navigate(RUTA_DEL_CHAT)}
                    data-testid="chat-flotante" aria-label={unread > 0 ? `Chat, ${unread} sin leer` : 'Chat'} title="Chat"
                    // Solo en el ordenador: en el teléfono el chat está en la cabecera (16-10).
                    className="hidden lg:flex fixed right-8 bottom-8 z-40 w-14 h-14 rounded-full bg-brand text-white shadow-lg shadow-black/40 items-center justify-center hover:bg-brand/90 transition-colors">
                    <MessageCircle className="w-6 h-6" strokeWidth={2.2} />
                    {unread > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-white text-brand text-[11px] font-bold flex items-center justify-center border-2 border-brand">
                            {unread}
                        </span>
                    )}
                </button>
            )}

            {/* ===== Mobile bottom nav ===== */}
            {/* LA BARRA NUEVA DEL DOC (21-08): Inicio · Mi semana · Rutina · Más, detrás
                del MISMO interruptor que el Inicio nuevo (t1_inicio_nuevo) para que el
                lunes se encienda todo junto. Apagado, la barra de siempre, sin cambios. */}
            {pantalla('t1_inicio_nuevo') ? (
                <BottomNav items={[...navArriba, ...navAbajo]} unread={unread} fichaPendiente={fichaPendiente}
                    onAvisos={openNotifications} avisosPendientes={notifCount + unread}
                    onVolverAlPanel={isStaff ? () => navigate('/admin') : null} onCerrarSesion={handleLogout} />
            ) : (
            <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-ink border-t border-white/10 barra-fija-abajo" data-testid="mobile-bottom-nav">
                <div className="flex items-stretch h-16">
                    {bottomItems.map(item => (
                        <NavLink key={item.path} to={item.path} end={item.end}
                            className={({ isActive }) => `flex flex-col items-center justify-center flex-1 min-w-0 gap-1 px-0.5 transition-colors ${isActive ? 'text-brand' : 'text-white/55'}`}
                            data-testid={`bottomnav-${slug(item.label)}`}>
                            {({ isActive }) => (<>
                                <item.icon className="w-[22px] h-[22px] flex-shrink-0" strokeWidth={isActive ? 2.5 : 2} />
                                {/* `min-w-0` y `truncate`: sin eso, «Seguimiento» marca el ancho
                                    mínimo de su cuarto de barra y en un móvil estrecho la barra
                                    entera se pasa de la pantalla. Medido a 280 px. */}
                                <span className={`text-[10px] max-w-full truncate ${isActive ? 'font-bold' : 'font-medium'}`}>{item.label}</span>
                            </>)}
                        </NavLink>
                    ))}
                    {/* SIN «MÁS» (documento del 10-08): eran cuatro pestañas y un cajón que
                        escondía media app detrás de una palabra que no dice qué hay dentro.
                        Lo que había en el cajón vive ahora en Perfil, con su nombre.
                        El cajón sigue existiendo para el escritorio, donde es la barra
                        lateral entera y ahí sí tiene sentido.
                        (Con `t1_inicio_nuevo` encendido manda la barra nueva de arriba,
                        que vuelve a traer «Más»: es lo que piden los mockups del 21-08.) */}
                </div>
            </nav>
            )}

            {/* ===== Panel de avisos (campanita) ===== */}
            {notifOpen && (
                <div className="fixed inset-0 z-[80] flex items-start justify-center p-4 pt-16 lg:pt-24" data-testid="notif-panel">
                    <div className="absolute inset-0 bg-black/50 animate-fade-in" onClick={() => setNotifOpen(false)} />
                    <div className="relative bg-card border border-border rounded-2xl shadow-xl w-full max-w-sm sm:max-w-xl max-h-[70vh] flex flex-col animate-slide-up">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                            <p className="font-bold text-foreground uppercase tracking-wider text-sm">Avisos</p>
                            <button onClick={() => setNotifOpen(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {unread > 0 && (
                                <button onClick={() => { setNotifOpen(false); navigate('/dashboard/messages'); }}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted border-b border-border">
                                    <Bell className="w-4 h-4 text-brand flex-shrink-0" />
                                    <span className="text-sm text-foreground flex-1">Tienes {unread} mensaje{unread > 1 ? 's' : ''} sin leer</span>
                                </button>
                            )}
                            {/* TODOS LOS AVISOS, EN UNA SOLA LISTA (rediseño del 22-10): sin los
                                tres grupos y sin «Ver todos los avisos». Del más nuevo al más
                                viejo, que es como los manda el servidor; los atendidos y los de
                                más de seis días ya no vienen. Desde el 26-10, como una bandeja
                                de correo y solo los diez últimos (`renderAviso`). */}
                            {notifItems.length > 0 && (
                                <div data-testid="avisos-lista">
                                    {/* La cabecera de la bandeja: las mismas columnas que cada fila. */}
                                    <div className={`${COLUMNAS_AVISOS} pl-3 pr-3 sm:pl-4 py-2 border-b border-border border-l-[3px] border-l-transparent bg-muted/40 text-[10px] font-bold uppercase tracking-wider text-brand`}>
                                        <span className="flex justify-center" title="Leído">
                                            <Check className="w-3.5 h-3.5 sm:hidden" strokeWidth={2.5} />
                                            <span className="hidden sm:inline">Leído</span>
                                        </span>
                                        <span>Fecha</span>
                                        <span>Aviso</span>
                                    </div>
                                    {/* Los diez como mucho los corta el servidor (`AVISOS_EN_LA_BANDEJA`). */}
                                    {notifItems.map(n => renderAviso(n))}
                                </div>
                            )}

                            {/* LA LISTA SE CIERRA DICIENDO QUE NO QUEDA NADA.
                                Una lista de avisos que solo termina cuando se acaban deja la
                                duda de si falta algo por cargar. Al abrir el panel ya se marcan
                                todos como leídos, así que el estado real es ese: no hay nada
                                más pendiente, y se dice (Jesús, 11-08). */}
                            {/* MIENTRAS SE BUSCAN, SE DICE. El panel se abre al instante y la
                                lista tarda lo que tarde el servidor (medido en desarrollo:
                                cinco segundos largos). Sin esta línea eran cinco segundos de
                                caja blanca vacía, que se lee como «no tengo nada» o como que
                                la app se ha colgado. Decir «no hay nada» todavía no se puede:
                                no se sabe. */}
                            {notifCargando ? (
                                <p className="text-muted-foreground text-sm text-center py-10">Buscando avisos...</p>
                            ) : notifItems.length === 0 && unread === 0 ? (
                                <p className="text-muted-foreground text-sm text-center py-10">No hay nada pendiente</p>
                            ) : null}
                        </div>
                    </div>
                </div>
            )}

            {/* ===== Mobile drawer ===== */}
            {drawerOpen && (
                <div className="lg:hidden fixed inset-0 z-[60]" data-testid="mobile-drawer">
                    <div className="absolute inset-0 bg-black/50 animate-fade-in" onClick={() => setDrawerOpen(false)} />
                    <div className="absolute inset-y-0 left-0 w-[82%] max-w-xs bg-ink flex flex-col animate-slide-up">
                        <div className="flex items-center justify-between h-14 px-4 border-b border-white/10">
                            <Logo12EN12 size="sm" tone="dark" />
                            <button onClick={() => setDrawerOpen(false)} data-testid="drawer-close"
                                className="w-9 h-9 rounded-lg flex items-center justify-center text-white/60 hover:bg-white/10">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
                            {navArriba.map(item => <SidebarLink key={item.path} item={item} collapsed={false} unread={unread} fichaPendiente={fichaPendiente} onClick={() => setDrawerOpen(false)} />)}
                        </nav>
                        <div className="mx-3 pt-3 pb-4 border-t border-white/10 space-y-1.5">
                            {navAbajo.map(item => <SidebarLink key={item.path} item={item} collapsed={false} unread={unread} fichaPendiente={fichaPendiente} onClick={() => setDrawerOpen(false)} />)}
                            {isStaff && (
                                <button onClick={() => navigate('/admin')}
                                    className="flex items-center gap-3.5 w-full px-4 py-3 rounded-xl font-semibold text-white/55 hover:text-brand hover:bg-brand/10 transition-colors">
                                    <LayoutDashboard className="w-5 h-5" /> <span className="text-[15px]">Volver al panel</span>
                                </button>
                            )}
                            <button onClick={handleLogout} className={claseDeSalir(false)}>
                                <LogOut className="w-5 h-5" /> <span className="text-[15px]">Cerrar sesión</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export { ClientDashboard, ClientLayout, PlanBadge, JG12Logo, MACRO };
